// src/pages/chat/ChatDetailPage.tsx

import { Fragment, useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useI18n } from '@react/hooks';
import type { TranslationKey, TranslationParams } from '@react/hooks';
import { chatApi, userprofileApi, getApiClient, tagApi } from '@core/api';
import type { Tag, TagSummary } from '@core/types/tag';
import { EntityAvatar } from '@/components/EntityAvatar';
import { TagPicker } from '@/components/TagPicker';
import type {
  Conversation,
  ConversationMember,
  Message,
  PinnedMessage,
  ReactionSummary,
} from '@core/types/chat';
import {
  Button,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import {
  conversationTypeMap,
  formatRelativeTime,
  formatMessageTime,
  uploadAttachment,
  getAttachmentDownloadUrl,
  isImageMimeType,
  isImageAttachment,
  formatFileSize,
  getFileExtension,
  resolveConversationDisplay,
} from '@/features/chat/logic';
import type { PendingAttachment } from '@/features/chat/logic';
import type { AttachmentPreview } from '@core/types/chat';
import { wsClient, WS_CHAT_ENABLED } from '@/features/chat/wsClient';
import { ResourceCommandMenu } from '@/components/ResourceCommandMenu';
import { ResourceCard } from '@/components/ResourceCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { MessageActionsMenu } from '@/features/chat/MessageActionsMenu';
import { Tooltip } from '@/components/Tooltip';
import { AttachmentBrowser } from '@/components/AttachmentBrowser';
import { attachmentApi } from '@core/api';
import { entityToRoute, entityToUrn, toResourceCardData } from '@/features/resources/logic';
import type { ResourceCardData } from '@/features/resources/types';
import { MessageSearchPanel } from '@/features/chat/MessageSearchPanel';
import { MentionTagsPanel } from '@/features/chat/MentionTagsPanel';
import { ConversationSummaryPanel } from '@/features/chat/ConversationSummaryPanel';
import { CreateTaskFromMessage } from '@/features/chat/CreateTaskFromMessage';
import {
  ScheduleSendButton,
  ScheduledChip,
  ScheduledMessagesBar,
} from '@/features/chat/ScheduleSendControls';

/** Resolved profile info for display in chat */
export interface ProfileInfo {
  displayName: string;
  avatarUrl: string;
}

/** The one ghost icon button used across the chat chrome: 28px, tonal hover. */
const ghostIconBtn =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none';

/** Clickable avatar + name component */
function UserAvatar({ profileId, profileMap, size = 'md' }: {
  profileId?: string;
  profileMap: Map<string, ProfileInfo>;
  size?: 'sm' | 'md';
}) {
  const info = profileId ? profileMap.get(profileId) : undefined;
  const name = info?.displayName || '?';
  const avatarUrl = info?.avatarUrl;
  const avatar = <EntityAvatar name={name} image={avatarUrl} profileId={profileId} size={size === 'sm' ? 'xs' : 'sm'} className={size === 'sm' ? 'h-7 w-7' : 'h-9 w-9'} />;

  if (!profileId) return avatar;
  return (
    <Link to={`/userprofiles/${profileId}`} className="flex-shrink-0 hover:opacity-80 transition-opacity">
      {avatar}
    </Link>
  );
}

/**
 * Clickable sender name. Deliberately sets no size: the surrounding message
 * header owns the scale, and a size baked in here silently overrode it — which
 * is how the same name came out at two sizes on two surfaces.
 */
function SenderName({ profileId, profileMap, fallback }: {
  profileId?: string;
  profileMap: Map<string, ProfileInfo>;
  fallback: string;
}) {
  const info = profileId ? profileMap.get(profileId) : undefined;
  const name = info?.displayName || fallback;

  if (!profileId) {
    return <span className="text-foreground">{name}</span>;
  }
  return (
    <Link to={`/userprofiles/${profileId}`} className="text-foreground hover:underline">
      {name}
    </Link>
  );
}

/** Quick emoji picker popup for composer */
const EMOJI_CATEGORIES = [
  { id: 'smileys', icon: '😀', emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😉', '😊', '😇', '🥰', '😍', '😘', '😗', '😋', '😛', '😜', '🤪', '😝', '🤗', '🤔', '🤭', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮', '😯', '😲', '😳', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '😈', '💀', '💩', '🤡', '👹', '👺', '👻', '👽', '🤖'] },
  { id: 'gestures', icon: '👍', emojis: ['👍', '👎', '👏', '🙌', '🤝', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘', '👌', '🤌', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '🤙', '💪', '🦾', '🙏', '✍️', '🤳', '💅'] },
  { id: 'objects', icon: '🚀', emojis: ['🚀', '✈️', '🎯', '🏆', '🥇', '🎨', '🎬', '🎵', '📷', '💎', '👑', '🎁', '🎉', '🎊', '✨', '🔥', '💡', '⚡', '💻', '🖥️', '📱', '🔗', '🛡️', '🔒', '🔑', '🐛', '🤖', '🧠', '📋', '📊', '📌', '📎'] },
  { id: 'symbols', icon: '❤️', emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💯', '✅', '❌', '⚠️', '🚫', '♻️', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⭐', '🌟', '💫', '🎖️'] },
];

function ComposerEmojiPicker({ onSelect, onClose }: {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}) {
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].id);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div ref={ref} className="absolute bottom-full left-0 mb-2 w-80 rounded-lg border border-border bg-popover shadow-lg z-50">
      <div className="flex gap-0.5 border-b border-border px-2 py-1">
        {EMOJI_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setActiveCategory(cat.id)}
            className={`rounded px-2 py-1 text-base transition-colors ${
              activeCategory === cat.id ? 'bg-accent' : 'hover:bg-accent/50'
            }`}
          >
            {cat.icon}
          </button>
        ))}
      </div>
      <div className="max-h-48 overflow-y-auto p-2">
        <div className="grid grid-cols-8 gap-0.5">
          {(EMOJI_CATEGORIES.find(c => c.id === activeCategory)?.emojis ?? []).map((emoji, i) => (
            <button
              key={`${emoji}-${i}`}
              type="button"
              onClick={() => { onSelect(emoji); onClose(); }}
              className="flex h-8 w-8 items-center justify-center rounded text-lg hover:bg-accent"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Renders attachment previews for a message */
function MessageAttachments({ previews }: {
  previews: AttachmentPreview[];
}) {
  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({});

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      let url = downloadUrls[attachmentId];
      if (!url) {
        url = await getAttachmentDownloadUrl(attachmentId);
        setDownloadUrls(prev => ({ ...prev, [attachmentId]: url }));
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
    } catch {
      // silent
    }
  };

  const getImageUrl = async (attachmentId: string): Promise<string> => {
    if (downloadUrls[attachmentId]) return downloadUrls[attachmentId];
    const url = await getAttachmentDownloadUrl(attachmentId);
    setDownloadUrls(prev => ({ ...prev, [attachmentId]: url }));
    return url;
  };

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {previews.map((preview) => {
        const attachmentId = preview.attachment?.id;
        if (!attachmentId) return null;

        if (isImageAttachment(preview)) {
          return (
            <ImagePreview
              key={attachmentId}
              preview={preview}
              getUrl={() => getImageUrl(attachmentId)}
            />
          );
        }

        return (
          <button
            key={attachmentId}
            onClick={() => handleDownload(attachmentId, preview.file_name)}
            className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm hover:bg-accent transition-colors max-w-xs"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-xs font-bold text-primary flex-shrink-0">
              {getFileExtension(preview.file_name) || '?'}
            </div>
            <div className="min-w-0 text-left">
              <p className="truncate font-medium text-foreground">{preview.file_name}</p>
              <p className="text-xs text-muted-foreground">{formatFileSize(preview.size_bytes)}</p>
            </div>
            <svg className="h-4 w-4 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

/** Lazy-loaded image preview */
function ImagePreview({ preview, getUrl }: {
  preview: AttachmentPreview;
  getUrl: () => Promise<string>;
}) {
  const [url, setUrl] = useState(preview.thumbnail_url ?? '');
  const [isLoading, setIsLoading] = useState(!preview.thumbnail_url);

  useEffect(() => {
    if (!preview.thumbnail_url) {
      getUrl().then(u => { setUrl(u); setIsLoading(false); }).catch(() => setIsLoading(false));
    }
  }, [preview.thumbnail_url]);

  if (isLoading) {
    return (
      <div className="h-40 w-60 animate-pulse rounded-lg bg-muted" />
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block max-w-[min(20rem,100%)] overflow-hidden rounded-lg border border-border hover:opacity-90 transition-opacity"
    >
      <img
        src={url}
        alt={preview.file_name}
        className="max-h-60 w-full rounded-lg object-cover"
      />
    </a>
  );
}

/** Pending attachment card in composer */
function PendingAttachmentCard({ attachment, onRemove }: {
  attachment: PendingAttachment;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="relative flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-2 py-1.5 text-sm">
      {attachment.previewUrl ? (
        <img src={attachment.previewUrl} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0" />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10 text-xs font-bold text-primary flex-shrink-0">
          {getFileExtension(attachment.file.name) || '?'}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-foreground">{attachment.file.name}</p>
        <p className="text-xs text-muted-foreground">{formatFileSize(attachment.file.size)}</p>
        {attachment.progress < 100 && !attachment.error && (
          <div className="mt-0.5 h-1 w-full rounded-full bg-muted">
            <div
              className="h-1 rounded-full bg-primary transition-all"
              style={{ width: `${attachment.progress}%` }}
            />
          </div>
        )}
        {attachment.error && (
          <p className="text-xs text-destructive">{attachment.error}</p>
        )}
      </div>
      <button
        onClick={() => onRemove(attachment.id)}
        className="rounded p-0.5 text-muted-foreground hover:text-destructive"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// ============================================
// Sub-components
// ============================================

/**
 * Extract a millisecond timestamp from a message's created_at. Handles both
 * proto Timestamp shape ({ seconds, nanos }) and raw string/number forms.
 * Returns 0 if absent — callers treat zero as "no boundary crossing".
 */
function getMessageTimeMs(msg: Message): number {
  const ca: any = msg.created_at;
  if (!ca) return 0;
  if (typeof ca === 'number') return ca;
  if (typeof ca === 'string') {
    const t = Date.parse(ca);
    return Number.isFinite(t) ? t : 0;
  }
  if (typeof ca === 'object' && typeof ca.seconds === 'number') {
    return ca.seconds * 1000 + Math.floor((ca.nanos ?? 0) / 1_000_000);
  }
  return 0;
}

/** Returns true iff ms1 and ms2 fall on the same local calendar day. */
function sameLocalDay(ms1: number, ms2: number): boolean {
  if (ms1 === 0 || ms2 === 0) return false;
  const a = new Date(ms1);
  const b = new Date(ms2);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** t() function signature from @react/hooks — matches useI18n's return type. */
type TFunc = (key: TranslationKey, options?: TranslationParams) => string;

/**
 * Formats a date-separator label. "Today" / "Yesterday" come from i18n
 * (chat.daySeparator.today / .yesterday). Older dates fall through to
 * Intl.DateTimeFormat which handles locale-specific month names.
 */
function formatDaySeparator(tsMs: number, locale: string, t: TFunc): string {
  const d = new Date(tsMs);
  const startOfDay = (date: Date) => {
    const x = new Date(date);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const today = startOfDay(new Date());
  const yesterday = today - 24 * 60 * 60 * 1000;
  const dayOfMsg = startOfDay(d);

  if (dayOfMsg === today) return t('chat.daySeparator.today');
  if (dayOfMsg === yesterday) return t('chat.daySeparator.yesterday');

  // Localised "Month Day" for this year, "Month Day, Year" for older.
  const thisYear = new Date().getFullYear();
  return new Intl.DateTimeFormat(locale || 'en', {
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() !== thisYear ? { year: 'numeric' } : {}),
  }).format(d);
}

/**
 * Format the typing-indicator banner via i18n. Clamps at 2 named typers to
 * keep the banner short; 3+ collapses to chat.typing.several.
 */
function formatTypingLabel(names: string[], t: TFunc): string {
  if (names.length === 0) return '';
  if (names.length === 1) return t('chat.typing.single', { name: names[0] });
  if (names.length === 2) return t('chat.typing.two', { a: names[0], b: names[1] });
  return t('chat.typing.several');
}

/**
 * Render a raw message body as a React node with minimal rich-content
 * support:
 *   - Triple-backtick fences → <pre><code> block (optional language hint
 *     captured but not yet syntax-highlighted)
 *   - `inline code` → <code> pill
 *   - http(s):// URLs → clickable <a target="_blank" rel>
 *
 * Deliberately does NOT:
 *   - support dangerouslySetInnerHTML (XSS surface)
 *   - support full markdown (images, lists, headings, blockquotes) —
 *     that can grow later into a proper markdown-it pipeline
 *   - auto-link bare domains without a scheme
 *
 * Algorithm: three-pass tokeniser.
 *   1. Split on code fences, yielding alternating {text, fence} segments.
 *   2. For each text segment, split on single-backtick inline code.
 *   3. For each remaining text subsegment, split on URL regex.
 * This keeps precedence intuitive: fences > inline code > URLs > plain text.
 */
const CODE_FENCE_RE = /```([a-zA-Z0-9_+-]*)\n?([\s\S]*?)```/g;
const INLINE_CODE_RE = /`([^`\n]+)`/g;
const URL_RE = /\bhttps?:\/\/[^\s<>"'`]+/g;

function renderRichContent(content: string): ReactNode[] {
  if (!content) return [];
  const nodes: ReactNode[] = [];
  let fenceLast = 0;
  let fenceMatch: RegExpExecArray | null;
  CODE_FENCE_RE.lastIndex = 0;
  while ((fenceMatch = CODE_FENCE_RE.exec(content)) !== null) {
    if (fenceMatch.index > fenceLast) {
      renderInlineInto(nodes, content.slice(fenceLast, fenceMatch.index), `t${fenceLast}`);
    }
    const lang = fenceMatch[1] || undefined;
    const code = fenceMatch[2].replace(/\n$/, '');
    nodes.push(
      <pre
        key={`fence-${fenceMatch.index}`}
        className="my-1 overflow-x-auto rounded-md bg-foreground/5 p-2 text-xs font-mono leading-snug"
        data-lang={lang}
      >
        <code>{code}</code>
      </pre>,
    );
    fenceLast = CODE_FENCE_RE.lastIndex;
  }
  if (fenceLast < content.length) {
    renderInlineInto(nodes, content.slice(fenceLast), `tail`);
  }
  return nodes;
}

/** Phase 2: inline code + URLs inside a plain text segment. */
function renderInlineInto(out: ReactNode[], text: string, keyPrefix: string) {
  if (!text) return;
  let codeLast = 0;
  let codeMatch: RegExpExecArray | null;
  INLINE_CODE_RE.lastIndex = 0;
  let i = 0;
  while ((codeMatch = INLINE_CODE_RE.exec(text)) !== null) {
    if (codeMatch.index > codeLast) {
      renderLinksInto(out, text.slice(codeLast, codeMatch.index), `${keyPrefix}-p${i}`);
    }
    out.push(
      <code
        key={`${keyPrefix}-c${i}`}
        className="rounded bg-foreground/10 px-1 py-0.5 font-mono text-[0.85em]"
      >
        {codeMatch[1]}
      </code>,
    );
    codeLast = INLINE_CODE_RE.lastIndex;
    i++;
  }
  if (codeLast < text.length) {
    renderLinksInto(out, text.slice(codeLast), `${keyPrefix}-tail`);
  }
}

/** Phase 3: auto-link http(s) URLs inside a plain text subsegment. */
function renderLinksInto(out: ReactNode[], text: string, keyPrefix: string) {
  if (!text) return;
  let urlLast = 0;
  let urlMatch: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  let i = 0;
  while ((urlMatch = URL_RE.exec(text)) !== null) {
    if (urlMatch.index > urlLast) {
      out.push(<span key={`${keyPrefix}-s${i}`}>{text.slice(urlLast, urlMatch.index)}</span>);
    }
    const href = urlMatch[0];
    out.push(
      <a
        key={`${keyPrefix}-u${i}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="underline underline-offset-2 hover:no-underline"
      >
        {href}
      </a>,
    );
    urlLast = URL_RE.lastIndex;
    i++;
  }
  if (urlLast < text.length) {
    out.push(<span key={`${keyPrefix}-tail`}>{text.slice(urlLast)}</span>);
  }
}

/**
 * Animated three-dot indicator. Pure CSS (Tailwind) to avoid new deps.
 */
function TypingDots() {
  return (
    <span className="inline-flex gap-0.5 items-end">
      <span className="h-1 w-1 rounded-full bg-current animate-pulse" style={{ animationDelay: '0ms' }} />
      <span className="h-1 w-1 rounded-full bg-current animate-pulse" style={{ animationDelay: '150ms' }} />
      <span className="h-1 w-1 rounded-full bg-current animate-pulse" style={{ animationDelay: '300ms' }} />
    </span>
  );
}

/**
 * Date divider rendered between message groups that cross a local-day
 * boundary: a hairline with a centered muted label, no pill.
 */
function DaySeparator({ label }: { label: string }) {
  return (
    <div className="my-3 flex items-center gap-3 px-4" aria-hidden="true">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

/**
 * The one way a message is drawn. Everywhere a message appears — the main
 * list, a thread, the quote inside a reply — goes through here, so that the
 * same message cannot end up in two typographic languages depending on which
 * surface it landed on.
 */
const MessageItem = observer(({
  message,
  locale,
  profileMap,
  isOwn,
  showSender,
  onReply,
  onQuickReply,
  onEdit,
  onDelete,
  onPin,
  onReaction,
  onLink,
  onCreateTask,
  linkedResources,
  onUnlinkResource,
  onTag,
  tags,
  selectionMode,
  isSelected,
  onToggleSelect,
  onCopyLink,
  onShowReaders,
  highlighted,
}: {
  message: Message;
  locale: string;
  profileMap: Map<string, ProfileInfo>;
  isOwn: boolean;
  showSender: boolean;
  onReply: (msg: Message) => void;
  onQuickReply?: (msg: Message) => void;
  onEdit: (msg: Message) => void;
  onDelete: (msg: Message) => void;
  onPin: (msg: Message) => void;
  onReaction: (msg: Message, emoji: string) => void;
  onLink?: (msg: Message) => void;
  onCreateTask?: (msg: Message) => void;
  linkedResources?: ResourceCardData[];
  onUnlinkResource?: (msg: Message, card: ResourceCardData) => void;
  onTag?: (msg: Message) => void;
  tags?: TagSummary[];
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (msg: Message) => void;
  onCopyLink?: (msg: Message) => void;
  onShowReaders?: (msg: Message) => void;
  highlighted?: boolean;
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const reactionButtonRef = useRef<HTMLButtonElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const senderId = message.sender?.entity?.id;
  // Never surface a raw entity UUID as a person's name — resolve to the
  // sender's display name, then a short friendly form, and only then a
  // translated "unknown" label. A 36-char id in the name slot is the single
  // ugliest thing in the thread.
  const senderFallback = message.sender?.display_name?.trim() || t('chat.unknownUser');
  const quickEmojis = ['👍', '❤️', '😂', '🎉', '😮', '😢'];

  const replyTo = message.reply_to;
  const replyToSenderId = replyTo?.sender?.entity?.id;
  const replyToSenderName = (replyToSenderId && profileMap.get(replyToSenderId)?.displayName)
    || replyTo?.sender?.display_name
    || '';
  const replyToSnippet = replyTo?.content_snippet;
  const showQuote = !!replyTo && !!(replyToSnippet || replyToSenderName);
  const forwardedFrom = message.forwarded_from;
  const forwardedFromSenderId = forwardedFrom?.original_sender?.entity?.id;
  const forwardedFromSenderName = (forwardedFromSenderId && profileMap.get(forwardedFromSenderId)?.displayName)
    || forwardedFrom?.original_sender?.display_name
    || '';

  const handleRowClick = (e: React.MouseEvent) => {
    // Cmd/Ctrl+click → enter or extend selection. Plain clicks inside
    // selection mode also toggle, so once the user is selecting they
    // can tap further rows without holding the modifier.
    if (e.metaKey || e.ctrlKey || selectionMode) {
      e.preventDefault();
      onToggleSelect?.(message);
    }
  };

  return (
    <div
      data-message-id={message.entity?.id}
      className={`group relative flex gap-3 px-4 ${showSender ? 'pt-3' : 'pt-0.5'} pb-0.5 transition-colors duration-150 motion-reduce:transition-none ${isOwn ? 'flex-row-reverse' : ''} ${
        isSelected ? 'bg-accent' : selectionMode ? 'cursor-pointer hover:bg-accent' : ''
      } ${highlighted ? 'bg-accent ring-1 ring-ring' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={handleRowClick}
    >
      {/* Avatar gutter — avatar on the first row of a group, empty spacer
          otherwise so subsequent lines align. Sits on the side the row
          starts from: left for others, right for own messages. */}
      <div className="flex w-9 flex-shrink-0 justify-center pt-0.5">
        {showSender ? (
          <UserAvatar profileId={senderId} profileMap={profileMap} />
        ) : null}
      </div>

      {/* Content column — the whole row flips to the opposite edge for own
          messages so the conversation reads as a two-sided dialogue. Only the
          bubble's position mirrors; everything inside it stays left-aligned,
          because mirroring the text as well is what made a reply and the
          message it quotes point in two different directions. */}
      <div className={`flex min-w-0 flex-1 flex-col ${isOwn ? 'items-end text-right' : 'items-start'}`}>
        {showSender && (
          <div className={`mb-1 flex items-baseline gap-2 ${isOwn ? 'flex-row-reverse pr-3' : 'pl-3'}`}>
            <span className="text-[13px] font-semibold text-foreground">
              <SenderName profileId={senderId} profileMap={profileMap} fallback={senderFallback} />
            </span>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {formatMessageTime(message.created_at, locale)}
            </span>
            {message.edited_at && (
              <span className="text-[11px] italic text-muted-foreground">({t('chat.edited')})</span>
            )}
          </div>
        )}
        {forwardedFromSenderName && (
          <div className={`mb-1 flex items-center gap-1 text-[11px] text-muted-foreground ${isOwn ? 'pr-3' : 'pl-3'}`}>
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a4 4 0 0 1 4 4v4m0 0-4-4m4 4 4-4" />
            </svg>
            <span>{t('chat.forwardedFrom', { name: forwardedFromSenderName })}</span>
          </div>
        )}
        {/* The bubble. A quote lives *inside* it, above the text: what you are
            replying to is part of your message, not a second message stacked
            on top of it. Outside, it had its own card, its own width and its
            own alignment, so one reply read as two things in two styles. */}
        {(message.content || showQuote) && (
          <div className="relative inline-block max-w-[85%]">
            <div
              className={`whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-left text-[13px] leading-[1.55] text-foreground ${
                isOwn ? 'bg-muted' : ''
              }`}
            >
              {showQuote && (
                <button
                  type="button"
                  onClick={() => {
                    const refId = replyTo?.entity?.id;
                    if (!refId) return;
                    const el = document.querySelector(`[data-message-id="${refId}"]`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el?.classList.add('ring-2', 'ring-ring');
                    window.setTimeout(() => el?.classList.remove('ring-2', 'ring-ring'), 1500);
                  }}
                  className="mb-1.5 flex w-full min-w-0 flex-col items-stretch rounded-md border border-border bg-card px-2 py-1 text-left transition-colors duration-150 hover:bg-accent motion-reduce:transition-none"
                >
                  <span className="truncate text-[11px] font-semibold text-foreground">
                    {replyToSenderName || t('chat.unknownUser')}
                  </span>
                  {replyToSnippet && (
                    <span className="truncate text-[11px] text-muted-foreground">
                      {replyToSnippet}
                    </span>
                  )}
                </button>
              )}
              {message.content && renderRichContent(message.content)}
              {!showSender && message.edited_at && (
                <span className="ml-1 text-[11px] italic text-muted-foreground">({t('chat.edited')})</span>
              )}
            </div>
          </div>
        )}

        {/* Attachment previews */}
        {message.attachment_previews?.items && message.attachment_previews.items.length > 0 && (
          <MessageAttachments previews={message.attachment_previews.items} />
        )}

        {/* Reactions */}
        {message.reactions?.items && message.reactions.items.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {message.reactions.items.map((r: ReactionSummary) => (
              <button
                key={r.emoji}
                onClick={() => onReaction(message, r.emoji)}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
                  r.me
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border bg-muted/50 text-muted-foreground hover:border-primary/30'
                }`}
              >
                <span>{r.emoji}</span>
                <span>{r.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Linked resources (framework-mew) — chips strip below content */}
        {linkedResources && linkedResources.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {linkedResources.map(card => (
              <ResourceCard
                key={card.urn}
                data={card}
                onUnlink={() => onUnlinkResource?.(message, card)}
                onNavigate={(d) => {
                  const route = entityToRoute({ type_name: d.typeName, type_id: d.typeId, id: d.id });
                  if (route) navigate(route);
                }}
              />
            ))}
          </div>
        )}

        {/* Tag chips (framework-zak) — small coloured pills below resources */}
        {tags && tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {tags.map(tag => (
              <span
                key={tag.id}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  tag.color ? 'text-white' : 'bg-muted text-muted-foreground'
                }`}
                style={tag.color ? { backgroundColor: tag.color } : undefined}
                title={tag.name}
              >
                #{tag.name}
              </span>
            ))}
          </div>
        )}

        {/* Read receipts (own messages only) — ✓ when at least one
            other member has read it, with a tooltip listing the latest
            reader timestamps. Shows ✓ on the sender side only; the
            backend already excludes the author from the count. */}
        {isOwn && (message.read?.count ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => onShowReaders?.(message)}
            className="mt-0.5 flex items-center gap-1 self-end text-[11px] text-muted-foreground hover:text-foreground"
            title={t('chat.readByCount', { count: message.read!.count })}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
            <span className="tabular-nums">{message.read!.count}</span>
          </button>
        )}

        {/* Thread info */}
        {(message.thread_info?.reply_count ?? 0) > 0 || message.is_thread_root ? (
          <button
            onClick={() => onReply(message)}
            className="mt-1.5 flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground motion-reduce:transition-none"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
            </svg>
            <span className="font-medium">
              {message.thread_info && message.thread_info.reply_count > 0
                ? t('chat.threadReplies', { count: message.thread_info.reply_count })
                : t('chat.thread')}
            </span>
            {message.thread_info?.last_reply_at && (
              <span className="text-muted-foreground">
                {formatRelativeTime(message.thread_info.last_reply_at, locale)}
              </span>
            )}
          </button>
        ) : null}
      </div>

      {/* Hover actions — a compact icon strip for the frequent ones (every
          icon carries a tooltip, an icon alone doesn't say what it does) plus
          a "more" menu whose items are fully labelled. Both popovers render
          into a body portal: a tall menu positioned inside the message list
          gets clipped by its overflow and scrolls along with the thread. */}
      {(showActions || showEmojiPicker || showMoreMenu) && (
        <div className={`absolute -top-3 z-20 flex items-center gap-0.5 rounded-lg border border-border bg-background px-1 py-0.5 shadow-sm ${isOwn ? 'right-4' : 'left-14'}`}>
          {/* Add reaction */}
          <Tooltip label={t('chat.addReaction')}>
            <button
              ref={reactionButtonRef}
              type="button"
              aria-label={t('chat.addReaction')}
              aria-haspopup="menu"
              aria-expanded={showEmojiPicker}
              onClick={() => { setShowMoreMenu(false); setShowEmojiPicker(v => !v); }}
              className={ghostIconBtn}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z" />
              </svg>
            </button>
          </Tooltip>
          {/* Quick inline reply (quoted) — sets composer reply chip */}
          {onQuickReply && (
            <Tooltip label={t('chat.reply')}>
              <button
                type="button"
                aria-label={t('chat.reply')}
                onClick={() => onQuickReply(message)}
                className={ghostIconBtn}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                </svg>
              </button>
            </Tooltip>
          )}
          {/* Reply in thread */}
          <Tooltip label={t('chat.replyInThread')}>
            <button
              type="button"
              aria-label={t('chat.replyInThread')}
              onClick={() => onReply(message)}
              className={ghostIconBtn}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
              </svg>
            </button>
          </Tooltip>
          {/* Everything else */}
          <Tooltip label={t('common.more')}>
            <button
              ref={moreButtonRef}
              type="button"
              aria-label={t('common.more')}
              aria-haspopup="menu"
              aria-expanded={showMoreMenu}
              onClick={() => { setShowEmojiPicker(false); setShowMoreMenu(v => !v); }}
              className={ghostIconBtn}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
              </svg>
            </button>
          </Tooltip>
        </div>
      )}

      {/* Quick reactions — one row next to the strip, not a wall of items */}
      <AnchoredMenu
        anchor={reactionButtonRef}
        open={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        align={isOwn ? 'end' : 'start'}
        ariaLabel={t('chat.addReaction')}
        className="flex items-center gap-1 p-1"
      >
        {quickEmojis.map(emoji => (
          <button
            key={emoji}
            type="button"
            role="menuitem"
            onClick={() => { onReaction(message, emoji); setShowEmojiPicker(false); }}
            className="rounded p-1 text-lg leading-none hover:bg-muted"
          >
            {emoji}
          </button>
        ))}
      </AnchoredMenu>

      <MessageActionsMenu
        anchor={moreButtonRef}
        open={showMoreMenu}
        onClose={() => setShowMoreMenu(false)}
        align={isOwn ? 'end' : 'start'}
        message={message}
        isOwn={isOwn}
        onCopyLink={onCopyLink}
        onPin={onPin}
        onLink={onLink}
        onCreateTask={onCreateTask}
        onTag={onTag}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
});

/** Thread panel sidebar */
function ThreadPanel({
  conversationId,
  parentMessage,
  locale,
  profileMap,
  onClose,
  onReaction,
  isMobile,
  currentProfileId,
}: {
  conversationId: string;
  parentMessage: Message;
  locale: string;
  profileMap: Map<string, ProfileInfo>;
  onClose: () => void;
  onReaction: (msg: Message, emoji: string) => void;
  isMobile?: boolean;
  /**
   * Needed for the same reason the main list needs it: without it every reply
   * in the panel was drawn as somebody else's, so your own message changed
   * sides and colour the moment you opened the thread it lives in.
   */
  currentProfileId?: string | null;
}) {
  const { t } = useI18n();
  const [replies, setReplies] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const loadReplies = useCallback(async () => {
    if (!parentMessage.entity?.id) return;
    setIsLoading(true);
    try {
      // REST only — see comment in loadMessages above for why ws_callable
      // read paths are off until the gateway forwards full scope context.
      const res = await chatApi.ListThreadReplies(conversationId, parentMessage.entity.id);
      setReplies(res.messages?.messages ?? []);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, parentMessage.entity?.id]);

  useEffect(() => { loadReplies(); }, [loadReplies]);

  // Live updates for thread replies. The chat service emits 'thread_reply'
  // on the same 'conv:{id}' channel that ChatDetailPage already subscribes
  // to — we add a second subscription scoped to the parent message so
  // ReplyInThread lands in the panel without a manual refetch. Edits and
  // deletes of replies come through as message_edited / message_deleted;
  // we reuse those to keep the panel coherent.
  useEffect(() => {
    if (!WS_CHAT_ENABLED) return;
    const parentId = parentMessage.entity?.id;
    if (!parentId) return;
    let cancelled = false;
    const unsub = wsClient.subscribe('conv:' + conversationId, {
      onThreadReply: (data: Message) => {
        if (cancelled || !data) return;
        // Only append replies whose parent matches this panel. A single
        // conversation can have many open threads across tabs; the channel
        // fans out to all of them.
        if (data.parent_message_id !== parentId && data.thread_id !== parentId) return;
        setReplies(prev => {
          const rid = data.entity?.id;
          if (rid && prev.some(r => r.entity?.id === rid)) {
            return prev.map(r => (r.entity?.id === rid ? data : r));
          }
          return [...prev, data];
        });
      },
      onMessageEdited: (data: Message) => {
        if (cancelled || !data) return;
        if (data.parent_message_id !== parentId && data.thread_id !== parentId) return;
        setReplies(prev => prev.map(r => (r.entity?.id === data.entity?.id ? data : r)));
      },
      onMessageDeleted: (payload: { message_id: string }) => {
        if (cancelled || !payload?.message_id) return;
        setReplies(prev => prev.filter(r => r.entity?.id !== payload.message_id));
      },
    });
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [conversationId, parentMessage.entity?.id]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !parentMessage.entity?.id) return;
    setIsSending(true);
    try {
      const res = await chatApi.ReplyInThread(conversationId, parentMessage.entity.id, {
        parent_message_id: parentMessage.entity.id,
        content: replyText.trim(),
      });
      // Optimistic local append so the author sees their message instantly
      // even before the WS broadcast returns. Dedup against the WS path.
      const newReply = res.message;
      if (newReply?.entity?.id) {
        setReplies(prev => {
          if (prev.some(r => r.entity?.id === newReply.entity?.id)) return prev;
          return [...prev, newReply];
        });
      }
      setReplyText('');
    } catch {
      // silent
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  return (
    <div className={`flex h-full flex-col bg-background ${isMobile ? 'w-full' : 'border-l border-border'}`}>
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        {isMobile ? (
          <button onClick={onClose} className={ghostIconBtn}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
        ) : null}
        <h3 className="flex-1 text-sm font-semibold text-foreground">{t('chat.thread')}</h3>
        {!isMobile ? (
          <button onClick={onClose} className={ghostIconBtn}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        ) : null}
      </div>

      {/* Thread root. Drawn by MessageItem like any other message — it used to
          be hand-rolled here as plain text at a different size, with a smaller
          avatar and no rich-content rendering, so the very message you had
          just clicked changed appearance on the way into the panel. */}
      <div className="flex-shrink-0 border-b border-border pb-2">
        <MessageItem
          message={parentMessage}
          locale={locale}
          profileMap={profileMap}
          isOwn={!!parentMessage.sender?.entity?.id && parentMessage.sender.entity.id === currentProfileId}
          showSender
          onReply={() => {}}
          onEdit={() => {}}
          onDelete={() => {}}
          onPin={() => {}}
          onReaction={onReaction}
        />
      </div>

      {/* Replies */}
      <div className="flex-1 overflow-x-hidden overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            {t('common.loading')}
          </div>
        ) : replies.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            {t('chat.noRepliesYet')}
          </div>
        ) : (
          replies.map((reply, idx) => {
            const prev = idx > 0 ? replies[idx - 1] : null;
            const senderId = reply.sender?.entity?.id;
            const sameSender = !!prev && !!senderId && prev.sender?.entity?.id === senderId;
            return (
              <MessageItem
                key={reply.entity?.id}
                message={reply}
                locale={locale}
                profileMap={profileMap}
                isOwn={!!senderId && senderId === currentProfileId}
                showSender={!sameSender}
                onReply={() => {}}
                onEdit={() => {}}
                onDelete={() => {}}
                onPin={() => {}}
                onReaction={onReaction}
              />
            );
          })
        )}
      </div>

      {/* Reply composer */}
      <div className="border-t border-border p-3">
        <div className="flex gap-2">
          <textarea
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.replyPlaceholder')}
            rows={1}
            className="flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground caret-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button
            size="sm"
            onClick={handleSendReply}
            disabled={!replyText.trim() || isSending}
            isLoading={isSending}
          >
            {t('chat.send')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Members panel sidebar */
function MembersPanel({
  conversationId,
  profileMap,
  onClose,
}: {
  conversationId: string;
  profileMap: Map<string, ProfileInfo>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [members, setMembers] = useState<ConversationMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    chatApi.ListConversationMembers(conversationId)
      .then(res => setMembers(res.members?.members ?? []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [conversationId]);

  const roleLabel = (role?: string) => {
    switch (role) {
      case 'CONVERSATION_ROLE_OWNER': return t('chat.role.owner');
      case 'CONVERSATION_ROLE_ADMIN': return t('chat.role.admin');
      case 'CONVERSATION_ROLE_MODERATOR': return t('chat.role.moderator');
      default: return '';
    }
  };

  return (
    <div className="flex h-full w-72 flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t('chat.members')} ({members.length})
        </h3>
        <button onClick={onClose} className={ghostIconBtn}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="h-8 w-8 rounded-full bg-muted" />
                <div className="h-4 w-24 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : (
          <ul className="py-1">
            {members.map(member => {
              const profileId = member.profile?.entity?.id;
              const fallbackName = member.profile?.display_name ?? member.profile?.entity?.id ?? '?';
              const role = roleLabel(member.role);
              return (
                <li key={member.entity?.id}>
                  <Link
                    to={profileId ? `/userprofiles/${profileId}` : '#'}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-accent transition-colors"
                  >
                    <UserAvatar profileId={profileId} profileMap={profileMap} size="sm" />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground">
                        {profileId ? (profileMap.get(profileId)?.displayName || fallbackName) : fallbackName}
                      </span>
                    </div>
                    {role && (
                      <Badge size="sm" variant="secondary">{role}</Badge>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Pinned messages modal */
function PinnedMessagesPanel({
  conversationId,
  locale,
  profileMap,
  messagesById,
  onClose,
  onUnpin,
  onJump,
}: {
  conversationId: string;
  locale: string;
  profileMap: Map<string, ProfileInfo>;
  messagesById: Map<string, Message>;
  onClose: () => void;
  onUnpin: (messageId: string) => void;
  onJump: (messageId: string) => void;
}) {
  const { t } = useI18n();
  const [pinnedMessages, setPinnedMessages] = useState<PinnedMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    chatApi.ListPinnedMessages(conversationId)
      .then(res => setPinnedMessages(res.pinned_messages?.pinned_messages ?? []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [conversationId]);

  return (
    <div className="flex h-full w-80 flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">{t('chat.pinnedMessages')}</h3>
        <button onClick={onClose} className={ghostIconBtn}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            {t('common.loading')}
          </div>
        ) : pinnedMessages.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            {t('chat.noPinnedMessages')}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {pinnedMessages.map(pm => {
              const msgId = pm.message?.id;
              const fullMsg = msgId ? messagesById.get(msgId) : undefined;
              const senderId = fullMsg?.sender?.entity?.id;
              const senderName = (senderId && profileMap.get(senderId)?.displayName)
                || fullMsg?.sender?.display_name
                || '?';
              return (
                <li
                  key={pm.entity?.id}
                  className="cursor-pointer px-4 py-3 transition-colors hover:bg-accent"
                  onClick={() => msgId && onJump(msgId)}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      {t('chat.pinnedBy', {
                        name: (pm.pinned_by?.entity?.id && profileMap.get(pm.pinned_by.entity.id)?.displayName)
                          || pm.pinned_by?.display_name || '?',
                      })}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (msgId) onUnpin(msgId); }}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      {t('chat.unpin')}
                    </button>
                  </div>
                  {fullMsg ? (
                    <>
                      <div className="mt-1 text-xs font-medium text-foreground">{senderName}</div>
                      <p className="mt-0.5 line-clamp-2 text-sm text-foreground">
                        {fullMsg.content || ''}
                      </p>
                    </>
                  ) : (
                    <p className="mt-1 text-xs italic text-muted-foreground">
                      {t('common.loading')}
                    </p>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeTime(pm.pinned_at, locale)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Conversation-wide attachments panel — aggregates all attachment
 * previews from the current messages list and renders images as a grid
 * with files as a plain list below. Client-side only for now: walks
 * local state rather than hitting a dedicated backend RPC. The panel
 * auto-refreshes when the parent passes a new messages array, so WS
 * new-message events that carry attachments show up instantly. */
function AttachmentsPanel({
  messages,
  locale,
  onClose,
  onJump,
}: {
  messages: Message[];
  locale: string;
  onClose: () => void;
  onJump: (messageId: string) => void;
}) {
  const { t } = useI18n();

  // Flatten previews + attach the owning message id so the tile can
  // scroll the main pane to the source on click.
  type Flat = {
    preview: AttachmentPreview;
    messageId: string;
    createdAt?: Message['created_at'];
  };
  const flat: Flat[] = [];
  for (const m of messages) {
    const mid = m.entity?.id;
    if (!mid) continue;
    for (const p of m.attachment_previews?.items ?? []) {
      flat.push({ preview: p, messageId: mid, createdAt: m.created_at });
    }
  }
  // Newest first — most recent attachments at the top.
  flat.sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tb - ta;
  });
  const images = flat.filter(f => isImageAttachment(f.preview));
  const files = flat.filter(f => !isImageAttachment(f.preview));

  return (
    <div className="flex h-full w-80 flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">{t('chat.attachments')}</h3>
        <button onClick={onClose} className={ghostIconBtn}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {flat.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            {t('chat.noAttachments')}
          </div>
        ) : (
          <div className="space-y-4 p-3">
            {images.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('chat.images')}
                </h4>
                <div className="grid grid-cols-3 gap-1.5">
                  {images.map((f, idx) => (
                    <button
                      key={`${f.messageId}-${f.preview.attachment?.id ?? idx}`}
                      onClick={() => onJump(f.messageId)}
                      className="group aspect-square overflow-hidden rounded-md border border-border bg-muted/30 transition-all hover:ring-2 hover:ring-ring"
                      title={f.preview.file_name}
                    >
                      {f.preview.thumbnail_url ? (
                        <img
                          src={f.preview.thumbnail_url}
                          alt={f.preview.file_name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">
                          {getFileExtension(f.preview.file_name) || '?'}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {files.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('chat.files')}
                </h4>
                <ul className="space-y-1">
                  {files.map((f, idx) => (
                    <li key={`${f.messageId}-${f.preview.attachment?.id ?? idx}`}>
                      <button
                        onClick={() => onJump(f.messageId)}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent"
                        title={f.preview.file_name}
                      >
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-primary/10 text-[10px] font-bold text-primary">
                          {getFileExtension(f.preview.file_name) || '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-foreground">
                            {f.preview.file_name}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {formatFileSize(f.preview.size_bytes)}
                            {f.createdAt && (
                              <> · {formatRelativeTime(f.createdAt, locale)}</>
                            )}
                          </p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Main Page
// ============================================

export const ChatDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkMessageId = searchParams.get('m');
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  // Guards the GetMessage fallback below from retrying forever once we've
  // already tried (and possibly failed) to resolve a given deep-linked id.
  const jumpAttemptedRef = useRef<string | null>(null);
  const { t, locale } = useI18n();

  // Conversation state
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoadingConv, setIsLoadingConv] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [cursor, setCursor] = useState('');

  // Profile map for displaying user info
  const [profileMap, setProfileMap] = useState<Map<string, ProfileInfo>>(new Map());
  // Raw members list — needed to resolve direct-conversation display name.
  const [conversationMembers, setConversationMembers] = useState<ConversationMember[]>([]);

  // Current user's active profile id — used to pick the "other" side of a DM.
  const auth = useAuth();

  // Composer state
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Scheduled-send mode: when set, Send stores scheduled_at instead of
  // sending now. Refresh key pokes the scheduled bar after a new schedule.
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [scheduledRefreshKey, setScheduledRefreshKey] = useState(0);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [showEmojiPickerComposer, setShowEmojiPickerComposer] = useState(false);
  const [attachmentBrowserOpen, setAttachmentBrowserOpen] = useState(false);

  // Edit state
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [editText, setEditText] = useState('');

  // Panel states
  const [activePanel, setActivePanel] = useState<'thread' | 'members' | 'pinned' | 'attachments' | 'search' | 'mentionTags' | 'summary' | null>(null);
  const [threadParent, setThreadParent] = useState<Message | null>(null);
  const [panelWidth, setPanelWidth] = useState(320);

  // Responsive
  const isMobile = useIsMobile();

  // Typing indicator (outbound: debounce keystrokes; inbound: map of
  // profile_id → expires_at_ms set by WS events, pruned on a 1s ticker).
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [typingMap, setTypingMap] = useState<Map<string, number>>(new Map());

  // Linked resources per message (framework-mew). Cache keyed by message
  // entity id. Populated in parallel after each ListMessages page lands.
  // null = "fetched, no links"; undefined = "not fetched yet".
  const [messageResources, setMessageResources] = useState<Map<string, ResourceCardData[]>>(
    new Map(),
  );

  // Resource picker modal state — message currently being linked.
  const [linkingMessage, setLinkingMessage] = useState<Message | null>(null);

  // Create-task-from-message dialog state — message being turned into a task.
  const [taskMessage, setTaskMessage] = useState<Message | null>(null);

  // Tags per message (framework-zak). One BatchGetEntityTags call per page
  // load enriches all visible messages — TagSummary is light enough
  // (id, name, color, icon) for the chip strip.
  const [messageTags, setMessageTags] = useState<Map<string, TagSummary[]>>(new Map());

  // Tag picker modal state — message currently being tagged.
  const [taggingMessage, setTaggingMessage] = useState<Message | null>(null);

  // Quick inline reply (quoted) — the message the next outgoing message
  // will quote. Lives until the user sends or cancels.
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  // Multi-select mode for bulk actions (forward, delete, copy). Entered
  // via Cmd/Ctrl+click on any message, exits on Esc or when selection
  // becomes empty.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Confirm dialog state — single slot, supports destructive variant and
  // async confirm. Closes on success or explicit cancel.
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    description?: string;
    confirmLabel?: string;
    destructive?: boolean;
    onConfirm: () => void | Promise<void>;
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Forward picker modal — list of target conversations the selected
  // messages will be sent to. null = closed.
  const [forwardPickerOpen, setForwardPickerOpen] = useState(false);
  const [forwardTargets, setForwardTargets] = useState<Set<string>>(new Set());
  const [forwardConversations, setForwardConversations] = useState<Conversation[]>([]);
  const [isForwarding, setIsForwarding] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);

  // ---- Data loading ----

  const loadConversation = useCallback(async () => {
    if (!id) return;
    setIsLoadingConv(true);
    setError(null);
    try {
      const res = await chatApi.GetConversation(id);
      setConversation(res.conversation ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chat.loadError'));
    } finally {
      setIsLoadingConv(false);
    }
  }, [id, t]);

  // Scroll anchor for lazy history loading. Captured just before a
  // prepend so a useLayoutEffect can restore the visual position once
  // React finishes rendering the new nodes. Without this the container
  // jumps to the top on every "load older" trigger, which makes infinite
  // scroll feel broken.
  const pendingScrollAnchorRef = useRef<{ prevHeight: number; prevTop: number } | null>(null);

  const loadMessages = useCallback(async (loadMore = false) => {
    if (!id) return;
    if (!loadMore) setIsLoadingMessages(true);
    try {
      const params = loadMore && cursor
        ? { pagination: { cursor, limit: 50 } }
        : { pagination: { limit: 50 } };
      // Read paths go over REST. The ws-gateway ws_callable stub for
      // ListMessages doesn't currently propagate tenant_id/platform_id
      // through authCtx, so the gRPC call into chat-svc loses scope and
      // returns an empty page — manifested as "history doesn't show
      // until I refresh the tab". HTTP middleware sets all context
      // vars from headers and works correctly. WS is still used for
      // write paths (SendMessage, AddReaction, etc.) and live pushes.
      const res = await chatApi.ListMessages(id, params as any);
      // Backend returns newest-first (ORDER BY created_at DESC) — we want
      // oldest-first for rendering so newest lands at the bottom.
      const newMessages = (res.messages?.messages ?? []).slice().reverse();

      if (loadMore) {
        const container = messagesContainerRef.current;
        if (container) {
          pendingScrollAnchorRef.current = {
            prevHeight: container.scrollHeight,
            prevTop: container.scrollTop,
          };
        }
        // Older page: prepend at the top.
        setMessages(prev => [...newMessages, ...prev]);
      } else {
        setMessages(newMessages);
      }
      setHasMoreMessages(res.has_more_before ?? false);
      const nextCursor = res.pagination?.page_info?.next_cursor;
      if (nextCursor) setCursor(nextCursor);
    } catch {
      // silent - conversation error already shown
    } finally {
      setIsLoadingMessages(false);
    }
  }, [id, cursor]);

  // Restore scroll position after a prepend from lazy-loading older
  // history. useLayoutEffect runs synchronously after DOM mutations so
  // the user never sees a flicker / jump.
  useLayoutEffect(() => {
    const anchor = pendingScrollAnchorRef.current;
    if (!anchor) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const delta = container.scrollHeight - anchor.prevHeight;
    container.scrollTop = anchor.prevTop + delta;
    pendingScrollAnchorRef.current = null;
  }, [messages]);

  // Load profiles for all members in the conversation
  const loadProfiles = useCallback(async () => {
    if (!id) return;
    try {
      const membersRes = await chatApi.ListConversationMembers(id);
      const members = membersRes.members?.members ?? [];
      const profileIds = members
        .map(m => m.profile?.entity?.id)
        .filter((pid): pid is string => !!pid);

      if (profileIds.length === 0) {
        setConversationMembers(members);
        return;
      }

      // Seed profileMap from Actor data already in members.
      const map = new Map<string, ProfileInfo>();
      for (const m of members) {
        const pid = m.profile?.entity?.id;
        if (pid) {
          // Seed with the member's own display name only — a raw profile id is
          // not a name and must never land in the map (SenderName renders it).
          map.set(pid, {
            displayName: m.profile?.display_name?.trim() || '',
            avatarUrl: m.profile?.avatar ?? '',
          });
        }
      }

      // Enrich with full profile data (better display names + avatar).
      const enrichment = new Map<string, string>();
      try {
        const res = await userprofileApi.ListUserprofilesByIDs({ ids: profileIds });
        const profiles = res.userprofiles?.profiles ?? [];
        for (const p of profiles) {
          if (!p.entity?.id) continue;
          const name = p.display_name?.trim()
            || [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
            || p.username?.trim()
            || '';
          // Backend enrichProfilesWithAvatars (list_userprofiles_by_ids.go)
          // packs the active avatar's full URL into `avatar.title` and
          // thumbnail URL into `avatar.icon`. Prefer thumbnail for chat.
          const avatarUrl =
            p.avatar?.icon?.trim() || p.avatar?.title?.trim() || '';
          if (name || avatarUrl) {
            if (name) enrichment.set(p.entity.id, name);
            const existing = map.get(p.entity.id);
            map.set(p.entity.id, {
              displayName: name || existing?.displayName || '',
              avatarUrl: avatarUrl || existing?.avatarUrl || '',
            });
          }
        }
      } catch {
        // Continue with raw member data.
      }
      setProfileMap(map);

      // Also write enriched display_name back into conversationMembers so the
      // shared display resolver can read them.
      const enrichedMembers = members.map(m => {
        const pid = m.profile?.entity?.id;
        const name = pid ? enrichment.get(pid) : undefined;
        if (!m.profile || !name) return m;
        return {
          ...m,
          profile: {
            ...m.profile,
            display_name: m.profile.display_name || name,
          },
        };
      });
      setConversationMembers(enrichedMembers);
    } catch {
      // fallback to Actor data from messages
    }
  }, [id]);

  useEffect(() => {
    loadConversation();
    loadMessages();
    loadProfiles();
  }, [id]);

  // Live updates via ws-gateway. See docs/architecture/ws-gateway.md.
  // On reconnect or tab regaining visibility, we run a catch-up fetch using
  // ListMessages(after_id=<last seen>) so no events are lost across gaps.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const mergeFresh = (fresh: Message[]) => {
      if (cancelled || fresh.length === 0) return;
      setMessages(prev => {
        const byId = new Map<string, Message>();
        for (const m of prev) {
          const mid = m.entity?.id;
          if (mid) byId.set(mid, m);
        }
        for (const m of fresh) {
          const mid = m.entity?.id;
          if (mid) byId.set(mid, m);
        }
        const next = Array.from(byId.values());
        next.sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (ta !== tb) return ta - tb;
          return (a.entity?.id ?? '').localeCompare(b.entity?.id ?? '');
        });
        return next;
      });
    };

    // WebSocket live subscription — gated behind VITE_ENABLE_WS_CHAT so we can
    // roll out safely. When disabled, the user still gets a fresh load on tab
    // focus (above); we just do not stream live events.
    // Note: there used to be a catch-up ListMessages call wired to the
    // 'reconnected' event. A flapping socket would fire that event over
    // and over, producing a /messages?after_id=... storm that showed up
    // in the network tab as obvious spam. We removed it entirely and now
    // rely on WS for live delivery. A proper replay-on-reconnect story
    // needs seq-based ordering on the server — tracked in framework-5ns
    // / framework-684.1.16.
    let unsub: (() => void) | undefined;
    // Clear typing state when switching conversations.
    setTypingMap(new Map());

    if (WS_CHAT_ENABLED) {
      unsub = wsClient.subscribe('conv:' + id, {
        onMessage: (data: Message) => {
          if (cancelled || !data) return;
          mergeFresh([data]);
          // A real message from this sender cancels their typing indicator.
          const senderId = data.sender?.entity?.id;
          if (senderId) {
            setTypingMap(prev => {
              if (!prev.has(senderId)) return prev;
              const next = new Map(prev);
              next.delete(senderId);
              return next;
            });
          }
        },
        onMessageEdited: (data: Message) => {
          if (cancelled || !data) return;
          setMessages(prev => prev.map(m => (m.entity?.id === data.entity?.id ? data : m)));
        },
        onMessageDeleted: (payload: { message_id: string }) => {
          if (cancelled || !payload?.message_id) return;
          setMessages(prev => prev.filter(m => m.entity?.id !== payload.message_id));
        },
        onTyping: (payload: { profile_id?: string }) => {
          if (cancelled || !payload?.profile_id) return;
          // Skip our own typing echo — don't show yourself typing.
          if (payload.profile_id === auth.activeProfileId) return;
          const expires = Date.now() + 5000;
          setTypingMap(prev => {
            const next = new Map(prev);
            next.set(payload.profile_id!, expires);
            return next;
          });
        },
        onReactionAdded: (data: any) => {
          if (cancelled || !data) return;
          // chat-svc broadcasts the full Reaction DTO (entity, message,
          // profile, emoji). Update the matching message's reaction
          // summary in place. The handler is idempotent for the
          // CURRENT user's own reactions: the optimistic chip from
          // handleReaction already incremented the count and set me=true,
          // so applying the WS echo on top would double-count. Skip
          // when isMe AND the chip is already in the "i reacted" state.
          const msgId = data.message?.id;
          const reactorId = data.profile?.id;
          const emoji = data.emoji;
          if (!msgId || !emoji) return;
          const isMe = reactorId === auth.activeProfileId;
          setMessages(prev => prev.map(m => {
            if (m.entity?.id !== msgId) return m;
            const items = [...(m.reactions?.items ?? [])];
            const idx = items.findIndex(r => r.emoji === emoji);
            if (idx >= 0) {
              const r = items[idx];
              if (isMe && r.me) return m; // already counted optimistically
              items[idx] = {
                ...r,
                count: r.count + 1,
                me: r.me || isMe,
              };
            } else {
              // Either a brand-new emoji from someone else, or our
              // optimistic insert hasn't landed yet (shouldn't happen
              // but treat as authoritative).
              items.push({ emoji, count: 1, reactor_ids: [], me: isMe });
            }
            return { ...m, reactions: { items } };
          }));
        },
        onReactionRemoved: (payload: { message_id?: string; profile_id?: string; emoji?: string }) => {
          if (cancelled || !payload?.message_id || !payload.emoji) return;
          const isMe = payload.profile_id === auth.activeProfileId;
          setMessages(prev => prev.map(m => {
            if (m.entity?.id !== payload.message_id) return m;
            const items = [...(m.reactions?.items ?? [])];
            const idx = items.findIndex(r => r.emoji === payload.emoji);
            if (idx < 0) return m;
            const r = items[idx];
            // Idempotent for self: if the optimistic remove already
            // dropped me=false on this chip, the WS echo would
            // double-decrement — skip.
            if (isMe && !r.me) return m;
            const next = {
              ...r,
              count: r.count - 1,
              me: isMe ? false : r.me,
            };
            if (next.count <= 0) items.splice(idx, 1);
            else items[idx] = next;
            return { ...m, reactions: { items } };
          }));
        },
        // Read receipts: backend publishes one event per MarkAsRead with
        // the ids of messages that newly got a receipt in this call.
        // Update local state so every client sees live "read by" counts.
        onMessagesRead: (payload: { profile_id?: string; newly_read_ids?: string[] }) => {
          if (cancelled || !payload?.newly_read_ids?.length || !payload.profile_id) return;
          const readerId = payload.profile_id;
          const isMe = readerId === auth.activeProfileId;
          const ts = new Date().toISOString();
          const markedSet = new Set(payload.newly_read_ids);
          setMessages(prev => prev.map(m => {
            if (!m.entity?.id || !markedSet.has(m.entity.id)) return m;
            const existing = m.read;
            // If this reader is already in the sample, nothing to do.
            const alreadyListed = existing?.reader_ids?.includes(readerId);
            if (alreadyListed) {
              // Just bump read_by_me if this was the caller and flag wasn't set.
              if (isMe && existing && !existing.read_by_me) {
                return { ...m, read: { ...existing, read_by_me: true } };
              }
              return m;
            }
            const nextCount = (existing?.count ?? 0) + 1;
            const nextReaderIds = [...(existing?.reader_ids ?? []), readerId].slice(0, 5);
            return {
              ...m,
              read: {
                count: nextCount,
                read_by_me: isMe || (existing?.read_by_me ?? false),
                reader_ids: nextReaderIds,
                last_read_at: ts,
              },
            };
          }));
        },
        // Resource graph events (framework-mew Phase 2). chat-worker fires
        // these when a resource.linked / resource.unlinked Kafka event from
        // resource-svc involves a message in this conversation. We
        // invalidate the local cache for that message so the next render
        // fetch effect re-runs and the chip strip refreshes via ListLinks.
        onResourceLinked: (payload: { message_id?: string }) => {
          if (cancelled || !payload?.message_id) return;
          setMessageResources(prev => {
            const next = new Map(prev);
            next.delete(payload.message_id!);
            return next;
          });
        },
        onResourceUnlinked: (payload: { message_id?: string }) => {
          if (cancelled || !payload?.message_id) return;
          setMessageResources(prev => {
            const next = new Map(prev);
            next.delete(payload.message_id!);
            return next;
          });
        },
      });

    }

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [id]);

  // Keep a ref to the latest messages so the ws-subscribe effect does not
  // need `messages` as a dep (avoiding a resubscribe on every new message).
  const messagesRef = useRef<Message[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Reset linked-resources + tag cache when switching conversations.
  useEffect(() => {
    setMessageResources(new Map());
    setMessageTags(new Map());
  }, [id]);

  // Batch-fetch tags for newly-arrived messages (framework-zak). Unlike
  // mew's per-message ListLinks (one HTTP call each), TagService exposes
  // BatchGetEntityTags which takes up to 100 entities in a single round-
  // trip. We only refetch ids that aren't yet in the cache, then merge.
  useEffect(() => {
    if (messages.length === 0) return;
    const fresh: Message[] = [];
    for (const m of messages) {
      const mid = m.entity?.id;
      if (!mid) continue;
      if (!messageTags.has(mid)) fresh.push(m);
    }
    if (fresh.length === 0) return;

    let cancelled = false;
    // Pre-mark to suppress re-firing while the request is in flight.
    setMessageTags(prev => {
      const next = new Map(prev);
      for (const m of fresh) {
        const mid = m.entity!.id!;
        if (!next.has(mid)) next.set(mid, []);
      }
      return next;
    });

    const targets = fresh
      .filter(m => m.entity?.id)
      .map(m => m.entity!);

    tagApi
      .BatchGetEntityTags({ targets: { entities: targets } as any } as any)
      .then(res => {
        if (cancelled) return;
        const map = res.entity_tags ?? {};
        setMessageTags(prev => {
          const next = new Map(prev);
          for (const m of fresh) {
            const mid = m.entity?.id;
            if (!mid) continue;
            // Backend keys by entity id (or URN); try both.
            const list = (map[mid] ?? map[`urn:corde:::${m.entity!.type_id}:${mid}`]);
            const items = list?.tags?.items ?? [];
            next.set(mid, items);
          }
          return next;
        });
      })
      .catch(() => {
        // Swallow — leave entries as empty arrays; user can retry by
        // re-opening the conversation.
      });

    return () => {
      cancelled = true;
    };
  }, [messages]);

  // Lazy-hydrate attachment previews. The chat service currently stores
  // attachments as entity refs only and does NOT populate
  // attachment_previews on SendMessage/ListMessages — so our MessageItem
  // would render nothing for files that were clearly sent. We resolve
  // file_name/mime/size via attachmentApi.GetAttachment per entity id
  // and splice a synthesized preview list into the local message state.
  // A set of already-hydrated message ids prevents re-firing on each
  // render. A future batch RPC would replace the N-call loop.
  const [hydratedAttachments, setHydratedAttachments] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (messages.length === 0) return;
    const pending: Array<{ msgId: string; entityIds: string[] }> = [];
    for (const m of messages) {
      const mid = m.entity?.id;
      if (!mid || hydratedAttachments.has(mid)) continue;
      const ents = m.attachments?.entities ?? [];
      if (ents.length === 0) continue;
      const haveAllPreviews = (m.attachment_previews?.items?.length ?? 0) >= ents.length;
      if (haveAllPreviews) {
        // Nothing to fetch but mark so we don't re-check.
        setHydratedAttachments(prev => new Set(prev).add(mid));
        continue;
      }
      const ids = ents.map(e => e.id).filter((x): x is string => !!x);
      if (ids.length === 0) continue;
      pending.push({ msgId: mid, entityIds: ids });
    }
    if (pending.length === 0) return;

    let cancelled = false;
    // Pre-mark to prevent a re-run while promises are in flight.
    setHydratedAttachments(prev => {
      const next = new Set(prev);
      for (const p of pending) next.add(p.msgId);
      return next;
    });

    (async () => {
      for (const { msgId, entityIds } of pending) {
        const previews: AttachmentPreview[] = [];
        for (const aid of entityIds) {
          try {
            const res = await attachmentApi.GetAttachment(aid);
            const att = res.attachment;
            if (!att?.entity?.id) continue;
            previews.push({
              attachment: att.entity,
              file_name: att.file_name,
              mime_type: att.mime_type,
              size_bytes: att.size_bytes,
              type: att.attachment_type,
              thumbnail_url: att.media?.thumbnail_url,
              thumbnail_width: att.media?.thumbnail_width,
              thumbnail_height: att.media?.thumbnail_height,
            } as AttachmentPreview);
          } catch {
            // Skip failed items; the remaining previews still render.
          }
        }
        if (cancelled || previews.length === 0) continue;
        setMessages(prev => prev.map(m => (
          m.entity?.id === msgId
            ? { ...m, attachment_previews: { items: previews } }
            : m
        )));
      }
    })();

    return () => { cancelled = true; };
  }, [messages, hydratedAttachments]);

  // Seed linked-resources cache from the server-populated
  // `message.resources` field. The backend hydrates it on ListMessages
  // (see enrichMessagesWithResources on chat-svc). We used to fire one
  // ListLinks RPC per message as a client-side fallback, which turned
  // into a 20-request burst on every cold-start and looked like a
  // spam loop to the user. Now: read what the server already returned,
  // no extra RPCs. LinkResources still populates the local cache
  // optimistically via handleResourceLinked.
  useEffect(() => {
    if (messages.length === 0) return;
    setMessageResources(prev => {
      let changed = false;
      const next = new Map(prev);
      for (const m of messages) {
        const mid = m.entity?.id;
        if (!mid || next.has(mid)) continue;
        const items = m.resources?.items ?? [];
        const cards = items.map(toResourceCardData);
        next.set(mid, cards);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [messages]);

  // Prune expired entries from typingMap once per second. Also runs when
  // the map becomes empty (no-op, but keeps the interval simple).
  useEffect(() => {
    const interval = window.setInterval(() => {
      setTypingMap(prev => {
        if (prev.size === 0) return prev;
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        for (const [pid, exp] of prev.entries()) {
          if (exp <= now) {
            next.delete(pid);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  // Resolve the current list of typer display names via profileMap.
  const typingLabel = (() => {
    if (typingMap.size === 0) return '';
    const names: string[] = [];
    for (const pid of typingMap.keys()) {
      const name = profileMap.get(pid)?.displayName ?? '…';
      names.push(name);
    }
    return formatTypingLabel(names, t);
  })();

  // "Stick to bottom" auto-scroll. Only follow new messages when the
  // user is already at (or very near) the bottom — otherwise scrolling
  // up to read older messages would yank the viewport back down on the
  // next WS push, and prepended history pages from loadMessages(true)
  // would look broken because the auto-scroll competes with the
  // useLayoutEffect anchor restoration. Tracked via a ref updated by
  // handleScroll so reads don't trigger re-renders.
  const isAtBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (isLoadingMessages) return;
    const prev = prevMessageCountRef.current;
    const curr = messages.length;
    prevMessageCountRef.current = curr;
    // First non-empty load — always scroll to the latest message.
    if (prev === 0 && curr > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      return;
    }
    // Subsequent appends — only follow if the user is still at the bottom.
    if (curr > prev && isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isLoadingMessages]);

  // Deep-link: if the URL has ?m=<id>, once that message is in the list,
  // scroll it into view and flash a highlight. Clear the query param so a
  // second visit or a reload doesn't keep re-triggering the scroll.
  useEffect(() => {
    if (!deepLinkMessageId || isLoadingMessages) return;
    const exists = messages.some(m => m.entity?.id === deepLinkMessageId);
    if (!exists) return;
    const el = document.querySelector(`[data-message-id="${deepLinkMessageId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(deepLinkMessageId);
      const tid = window.setTimeout(() => setHighlightedMessageId(null), 2000);
      // Clean the param so subsequent state changes don't re-fire the effect.
      const next = new URLSearchParams(searchParams);
      next.delete('m');
      setSearchParams(next, { replace: true });
      return () => window.clearTimeout(tid);
    }
  }, [deepLinkMessageId, messages, isLoadingMessages]);

  // Deep-link fallback: the message might be older than the default
  // window ListMessages loaded on mount (e.g. a link shared from
  // MessageSearchPanel, which can surface a hit from months back).
  // GetMessage confirms the message still exists (deleted messages 404
  // here, which is exactly when we should stop trying), then
  // ListMessages(around_id=...) rebuilds the window centered on it so
  // the highlight effect above finds it on the next render.
  useEffect(() => {
    if (!id || !deepLinkMessageId || isLoadingMessages) return;
    if (messages.some(m => m.entity?.id === deepLinkMessageId)) return;
    if (jumpAttemptedRef.current === deepLinkMessageId) return;
    jumpAttemptedRef.current = deepLinkMessageId;
    (async () => {
      try {
        await chatApi.GetMessage(id, deepLinkMessageId);
        const res = await chatApi.ListMessages(id, { around_id: deepLinkMessageId, pagination: { limit: 50 } } as any);
        const windowMessages = (res.messages?.messages ?? []).slice().reverse();
        if (windowMessages.length > 0) {
          setMessages(windowMessages);
          setHasMoreMessages(res.has_more_before ?? false);
        }
      } catch {
        // Message no longer exists (deleted) or is otherwise unreachable —
        // nothing to jump to, leave the current window as-is.
      }
    })();
  }, [id, deepLinkMessageId, messages, isLoadingMessages]);

  // Mark as read driven by IntersectionObserver on message rows.
  // Instead of pinging MarkAsRead once on mount (which drops read
  // receipts the user never actually scrolled past), we watch rows
  // enter the viewport and debounce a single call with the highest
  // visible, other-authored message id. Own messages are skipped —
  // you don't need a receipt for what you sent. The call is also
  // gated on document focus so background tabs don't spuriously
  // mark messages as read.
  const lastMarkedReadIdRef = useRef<string | null>(null);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!id || !conversation || !container) return;

    let pending: number | null = null;
    let highestVisible: { id: string; createdAt: string } | null = null;

    const flush = () => {
      pending = null;
      if (!highestVisible || !document.hasFocus()) return;
      if (lastMarkedReadIdRef.current === highestVisible.id) return;
      const targetId = highestVisible.id;
      lastMarkedReadIdRef.current = targetId;
      chatApi.MarkAsRead(id, { last_read_message_id: targetId }).catch(() => {
        // Roll back on failure so a retry can still advance.
        if (lastMarkedReadIdRef.current === targetId) {
          lastMarkedReadIdRef.current = null;
        }
      });
    };

    const schedule = () => {
      if (pending !== null) return;
      pending = window.setTimeout(flush, 400);
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target as HTMLElement;
        const msgId = el.getAttribute('data-message-id');
        if (!msgId) continue;
        const msg = messagesRef.current.find(m => m.entity?.id === msgId);
        if (!msg) continue;
        // Skip own messages — a sender doesn't "read" their own.
        if (msg.sender?.entity?.id === auth.activeProfileId) continue;
        const createdAt = msg.created_at || '';
        if (!highestVisible || createdAt > highestVisible.createdAt) {
          highestVisible = { id: msgId, createdAt };
        }
      }
      if (highestVisible) schedule();
    }, { root: container, threshold: 0.6 });

    // Observe existing rows + any rows added later via a MutationObserver.
    const observeRow = (el: Element) => observer.observe(el);
    container.querySelectorAll('[data-message-id]').forEach(observeRow);
    const mo = new MutationObserver((muts) => {
      for (const mut of muts) {
        mut.addedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) return;
          if (node.hasAttribute('data-message-id')) observeRow(node);
          node.querySelectorAll?.('[data-message-id]').forEach(observeRow);
        });
      }
    });
    mo.observe(container, { childList: true, subtree: true });

    const onFocus = () => { if (highestVisible) schedule(); };
    window.addEventListener('focus', onFocus);

    return () => {
      observer.disconnect();
      mo.disconnect();
      window.removeEventListener('focus', onFocus);
      if (pending !== null) window.clearTimeout(pending);
    };
  }, [id, conversation]);

  // ---- Actions ----

  const handleSendMessage = async () => {
    const hasText = messageText.trim().length > 0;
    const hasAttachments = pendingAttachments.length > 0;
    if ((!hasText && !hasAttachments) || !id) return;
    const scheduled = scheduledAt;

    setIsSending(true);
    try {
      // Upload any pending attachments that haven't been uploaded yet
      const attachmentEntityIds: string[] = [];
      for (const att of pendingAttachments) {
        if (att.entityId) {
          attachmentEntityIds.push(att.entityId);
        } else if (!att.error) {
          try {
            const { entityId } = await uploadAttachment(att.file, (progress) => {
              setPendingAttachments(prev =>
                prev.map(a => a.id === att.id ? { ...a, progress } : a)
              );
            });
            attachmentEntityIds.push(entityId);
          } catch {
            setPendingAttachments(prev =>
              prev.map(a => a.id === att.id ? { ...a, error: 'Upload failed' } : a)
            );
          }
        }
      }

      const res = await chatApi.SendMessage(id, {
        content: messageText.trim(),
        attachments: attachmentEntityIds.length > 0
          ? { entities: attachmentEntityIds.map(eid => ({ id: eid } as any)) }
          : undefined,
        reply_to_message_id: replyTo?.entity?.id,
        ...(scheduled ? { scheduled_at: scheduled.toISOString() } : {}),
      } as any);
      const newMsg = res.message;
      if (scheduled) {
        // Scheduled messages are stored server-side and published when due —
        // they don't belong in the live stream, only in the scheduled bar.
        setScheduledAt(null);
        setScheduledRefreshKey(k => k + 1);
      } else if (newMsg) {
        // Dedup against any WS-pushed copy of the same message that may have
        // arrived first (race between SendMessage RPC response and the
        // ws:deliver fan-out for our own send).
        setMessages(prev => {
          const id = newMsg.entity?.id;
          if (id && prev.some(m => m.entity?.id === id)) {
            return prev.map(m => (m.entity?.id === id ? newMsg : m));
          }
          return [...prev, newMsg];
        });
      }
      setMessageText('');
      setPendingAttachments([]);
      setReplyTo(null);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch {
      // silent
    } finally {
      setIsSending(false);
    }
  };

  const handleTyping = () => {
    if (!id) return;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      // Prefer the open WebSocket — typing is a fire-and-forget
      // ephemeral signal and shouldn't pay an HTTP RTT per keystroke
      // batch. Falls back to REST if the socket isn't open yet (cold
      // start race) or the flag is disabled.
      if (WS_CHAT_ENABLED && wsClient.isOpen) {
        wsClient.call('chat.send_typing_indicator', {
          conversation: { id } as any,
        }, 5000).catch(() => {});
        return;
      }
      chatApi.SendTypingIndicator(id, {}).catch(() => {});
    }, 500);
  };

  // ---- Quick reply / multi-select / forward ----

  // Global Esc: drop selection mode first, then reply chip.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (forwardPickerOpen) { setForwardPickerOpen(false); return; }
      if (selectionMode) { setSelectionMode(false); setSelectedIds(new Set()); return; }
      if (replyTo) { setReplyTo(null); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectionMode, replyTo, forwardPickerOpen]);

  const handleQuickReply = (msg: Message) => {
    setReplyTo(msg);
  };

  const handleCopyMessageLink = async (msg: Message) => {
    const msgId = msg.entity?.id;
    if (!msgId || !id) return;
    const url = `${window.location.origin}/chats/${id}?m=${msgId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // silent — clipboard perms
    }
  };

  // Readers modal: opened from the ✓ indicator on own messages. We
  // lazy-fetch the full reader list (with per-reader timestamps) only
  // when the user explicitly clicks — list_messages only hydrates the
  // aggregated count + sample ids.
  const [readersModal, setReadersModal] = useState<{
    message: Message;
    readers: { profile_id: string; read_at: string }[] | null;
    loading: boolean;
  } | null>(null);
  const handleShowReaders = async (msg: Message) => {
    const msgId = msg.entity?.id;
    if (!msgId || !id) return;
    setReadersModal({ message: msg, readers: null, loading: true });
    try {
      const res = await chatApi.ListMessageReaders(id, msgId);
      const readers = (res.readers ?? []).map((r: any) => ({
        profile_id: r.profile?.id ?? '',
        read_at: r.read_at ?? '',
      }));
      setReadersModal(cur => cur && cur.message.entity?.id === msgId
        ? { ...cur, readers, loading: false }
        : cur);
    } catch {
      setReadersModal(cur => cur && cur.message.entity?.id === msgId
        ? { ...cur, readers: [], loading: false }
        : cur);
    }
  };

  const handleCopyConversationLink = async () => {
    if (!id) return;
    const url = `${window.location.origin}/chats/${id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // silent
    }
  };

  const handleToggleSelect = (msg: Message) => {
    const msgId = msg.entity?.id;
    if (!msgId) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      if (next.size === 0) setSelectionMode(false);
      return next;
    });
    setSelectionMode(true);
  };

  const handleExitSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const getSelectedMessages = (): Message[] => {
    return messages.filter(m => m.entity?.id && selectedIds.has(m.entity.id));
  };

  const handleBulkCopy = async () => {
    const selected = getSelectedMessages();
    const text = selected
      .map(m => {
        const senderId = m.sender?.entity?.id;
        const name = (senderId && profileMap.get(senderId)?.displayName)
          || m.sender?.display_name
          || t('chat.unknownUser');
        return `${name}: ${m.content || ''}`;
      })
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // silent
    }
    handleExitSelection();
  };

  const handleBulkDelete = () => {
    if (!id) return;
    const selected = getSelectedMessages();
    if (selected.length === 0) return;
    setConfirmDialog({
      title: t('chat.deleteMessageConfirm'),
      destructive: true,
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        for (const msg of selected) {
          if (!msg.entity?.id) continue;
          try {
            await chatApi.DeleteMessage(id, msg.entity.id);
          } catch {
            // silent per-message; continue
          }
        }
        setMessages(prev => prev.filter(m => !(m.entity?.id && selectedIds.has(m.entity.id))));
        handleExitSelection();
      },
    });
  };

  const handleOpenForwardPicker = async () => {
    setForwardTargets(new Set());
    setForwardPickerOpen(true);
    try {
      const res = await chatApi.ListConversations({
        pagination: { limit: 100, cursor: '', direction: 'forward' },
        sort: [],
        view: 'default',
      } as any);
      setForwardConversations(res.conversations?.conversations ?? []);
    } catch {
      setForwardConversations([]);
    }
  };

  const handleToggleForwardTarget = (convId: string) => {
    setForwardTargets(prev => {
      const next = new Set(prev);
      if (next.has(convId)) next.delete(convId);
      else next.add(convId);
      return next;
    });
  };

  const handleConfirmForward = async () => {
    const selected = getSelectedMessages();
    if (selected.length === 0 || forwardTargets.size === 0) return;
    setIsForwarding(true);
    try {
      const messageIds = selected.map(m => m.entity?.id).filter((x): x is string => !!x);
      const targetIds = Array.from(forwardTargets);
      try {
        await chatApi.ForwardMessages(id ?? '', {
          message_ids: messageIds,
          target_conversation_ids: targetIds,
        });
      } catch {
        // silent
      }
    } finally {
      setIsForwarding(false);
      setForwardPickerOpen(false);
      setForwardTargets(new Set());
      handleExitSelection();
    }
  };

  const handleEditMessage = async () => {
    if (!editingMessage?.entity?.id || !editText.trim() || !id) return;
    try {
      const res = await chatApi.EditMessage(id, editingMessage.entity.id, {
        content: editText.trim(),
      });
      const updatedMsg = res.message;
      if (updatedMsg) {
        setMessages(prev => prev.map(m =>
          m.entity?.id === editingMessage.entity?.id ? updatedMsg : m
        ));
      }
      setEditingMessage(null);
      setEditText('');
    } catch {
      // silent
    }
  };

  const handleDeleteMessage = (msg: Message) => {
    if (!msg.entity?.id || !id) return;
    const targetId = msg.entity.id;
    setConfirmDialog({
      title: t('chat.deleteMessageConfirm'),
      destructive: true,
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        try {
          await chatApi.DeleteMessage(id, targetId);
          setMessages(prev => prev.filter(m => m.entity?.id !== targetId));
        } catch {
          // silent
        }
      },
    });
  };

  const handlePinMessage = async (msg: Message) => {
    if (!msg.entity?.id || !id) return;
    try {
      await chatApi.PinMessage(id, msg.entity.id, {});
    } catch {
      // silent
    }
  };

  const handleUnpinMessage = async (messageId: string) => {
    if (!id) return;
    try {
      await chatApi.UnpinMessage(id, messageId);
    } catch {
      // silent
    }
  };

  const handleReaction = async (msg: Message, emoji: string) => {
    if (!msg.entity?.id || !id) return;
    const msgId = msg.entity.id;
    const existing = msg.reactions?.items?.find(
      (r: ReactionSummary) => r.emoji === emoji && r.me
    );
    const adding = !existing;

    // Optimistic local update so the chip toggles instantly. The
    // reactions WS broadcast will arrive shortly after and overwrite
    // with the canonical state — by that time our local change should
    // match, so the user sees no flicker.
    setMessages(prev => prev.map(m => {
      if (m.entity?.id !== msgId) return m;
      const items = [...(m.reactions?.items ?? [])];
      const idx = items.findIndex(r => r.emoji === emoji);
      if (adding) {
        if (idx >= 0) {
          items[idx] = { ...items[idx], count: items[idx].count + 1, me: true };
        } else {
          items.push({ emoji, count: 1, reactor_ids: [], me: true });
        }
      } else {
        if (idx >= 0) {
          const next = { ...items[idx], count: items[idx].count - 1, me: false };
          if (next.count <= 0) items.splice(idx, 1);
          else items[idx] = next;
        }
      }
      return { ...m, reactions: { items } };
    }));

    // Prefer WS — both add_reaction and remove_reaction are ws_callable.
    // REST is the fallback when the socket isn't open yet.
    try {
      if (adding) {
        if (WS_CHAT_ENABLED && wsClient.isOpen) {
          await wsClient.call('chat.add_reaction', {
            conversation: { id } as any,
            message: { id: msgId } as any,
            emoji,
          }, 5000);
        } else {
          await chatApi.AddReaction(id, msgId, { emoji });
        }
      } else {
        if (WS_CHAT_ENABLED && wsClient.isOpen) {
          await wsClient.call('chat.remove_reaction', {
            conversation: { id } as any,
            message: { id: msgId } as any,
            emoji,
          }, 5000);
        } else {
          await chatApi.RemoveReaction(id, msgId, emoji);
        }
      }
    } catch {
      // silent — server-side failure will be corrected on next page reload.
    }
  };

  const handleStartEdit = (msg: Message) => {
    setEditingMessage(msg);
    setEditText(msg.content);
  };

  const handleOpenThread = (msg: Message) => {
    setThreadParent(msg);
    setActivePanel('thread');
  };

  // ---- Resource linking (framework-mew) ----

  const handleOpenLinkPicker = (msg: Message) => {
    setLinkingMessage(msg);
  };

  const handleOpenCreateTask = (msg: Message) => {
    setTaskMessage(msg);
  };

  const handleResourceLinked = useCallback((picked: any) => {
    // ResourceCommandMenu has already called LinkResources; we just need to
    // splice the new card into the local cache so the chip strip updates
    // without a refetch. The picker passes back a Resource shape.
    if (!linkingMessage) return;
    const mid = linkingMessage.entity?.id;
    if (!mid) return;
    const card = toResourceCardData(picked);
    setMessageResources(prev => {
      const next = new Map(prev);
      const cur = next.get(mid) ?? [];
      // Dedupe by urn so re-linking the same resource is idempotent.
      if (!cur.some(c => c.urn === card.urn)) {
        next.set(mid, [...cur, card]);
      }
      return next;
    });
    setLinkingMessage(null);
  }, [linkingMessage]);

  // ---- Message tagging (framework-zak) ----

  const handleOpenTagPicker = (msg: Message) => {
    setTaggingMessage(msg);
  };

  // Diff old vs new tag set on picker change. Called by TagPicker which
  // operates on full Tag[] objects (Tag has nested entity.id, NOT a flat id);
  // we keep the lighter TagSummary[] (with flat id) in local state for chip
  // rendering. Conversion handles the shape mismatch in both directions.
  const handleTagPickerChange = useCallback(async (newTags: Tag[]) => {
    if (!taggingMessage?.entity?.id || !taggingMessage?.entity?.type_id) return;
    const mid = taggingMessage.entity.id;
    const targetType = taggingMessage.entity.type_id;
    const targetEntity = taggingMessage.entity;
    const cur = messageTags.get(mid) ?? [];

    const curIds = new Set(cur.map(t => t.id));
    const newIds = new Set<string>();
    for (const t of newTags) {
      const tid = t.entity?.id;
      if (tid) newIds.add(tid);
    }

    const added = newTags.filter(t => {
      const tid = t.entity?.id;
      return tid && !curIds.has(tid);
    });
    const removed = cur.filter(t => !newIds.has(t.id));

    // Optimistic local update — picker stays open, user sees instant
    // feedback. On any error we just refetch; the BatchGetEntityTags
    // path on next conversation reload heals divergence anyway.
    const optimisticSummaries: TagSummary[] = newTags
      .filter(t => t.entity?.id)
      .map(t => ({
        id: t.entity!.id!,
        name: t.name,
        color: t.color,
        icon: t.icon,
        visibility: t.visibility,
      }));
    setMessageTags(prev => {
      const next = new Map(prev);
      next.set(mid, optimisticSummaries);
      return next;
    });

    // Fire all add/remove calls in parallel; failures are best-effort.
    // TagEntity uses POST with body {tag_id, target}; UntagEntity uses
    // DELETE with URL params (tag_id, entity_type, entity_id).
    const ops: Promise<unknown>[] = [];
    for (const t of added) {
      const tid = t.entity!.id!;
      ops.push(
        tagApi.TagEntity(tid, { tag_id: tid, target: targetEntity }).catch(() => {}),
      );
    }
    for (const t of removed) {
      ops.push(
        tagApi.UntagEntity(t.id, targetType, mid).catch(() => {}),
      );
    }
    await Promise.all(ops);
  }, [taggingMessage, messageTags]);

  const handleUnlinkResource = useCallback(async (msg: Message, card: ResourceCardData) => {
    const mid = msg.entity?.id;
    if (!mid) return;
    const sourceUrn = entityToUrn(msg.entity);
    if (!sourceUrn) return;
    // Optimistic remove from the cache; on failure we restore.
    const prevCards = messageResources.get(mid) ?? [];
    setMessageResources(prev => {
      const next = new Map(prev);
      next.set(mid, prevCards.filter(c => c.urn !== card.urn));
      return next;
    });
    try {
      // The auto-generated UnlinkResources wrapper at
      // frontend/core/api/endpoints/resource/unlink_resources.ts has lost
      // its body parameter (codegen bug — see follow-up needed). Bypass
      // and call the underlying ApiClient.delete with body in options
      // until the wrapper is regenerated correctly.
      const client = getApiClient();
      await (client as any).delete('/resource/resources/links', {
        body: JSON.stringify({
          source: { urn: sourceUrn },
          target: { urn: card.urn },
        }),
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      // Restore on failure.
      setMessageResources(prev => {
        const next = new Map(prev);
        next.set(mid, prevCards);
        return next;
      });
    }
  }, [messageResources]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Track stick-to-bottom: anything within ~80px of the bottom counts
    // so a small natural scroll wiggle doesn't make us drop off.
    const distFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
    isAtBottomRef.current = distFromBottom < 80;
    // Load older history when scrolled near top
    if (container.scrollTop < 100 && hasMoreMessages && !isLoadingMessages) {
      loadMessages(true);
    }
  };

  const handleFilesSelected = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const newAttachments: PendingAttachment[] = Array.from(files).map(file => {
      const att: PendingAttachment = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        progress: 0,
      };
      if (isImageMimeType(file.type)) {
        att.previewUrl = URL.createObjectURL(file);
      }
      return att;
    });

    setPendingAttachments(prev => [...prev, ...newAttachments]);

    // Start uploading immediately
    for (const att of newAttachments) {
      uploadAttachment(att.file, (progress) => {
        setPendingAttachments(prev =>
          prev.map(a => a.id === att.id ? { ...a, progress } : a)
        );
      }).then(({ entityId }) => {
        setPendingAttachments(prev =>
          prev.map(a => a.id === att.id ? { ...a, entityId, progress: 100 } : a)
        );
      }).catch(() => {
        setPendingAttachments(prev =>
          prev.map(a => a.id === att.id ? { ...a, error: 'Upload failed' } : a)
        );
      });
    }
  }, []);

  // Re-attach existing attachments picked from the user's library —
  // skip the upload step entirely, just add a PendingAttachment with
  // the entityId already set so handleSendMessage threads it through
  // unchanged. The synthetic File is only used for the preview chip
  // (name + size + type); the bytes never leave the server.
  const handleAttachExisting = useCallback((picks: Array<{ entityId: string; file: { name: string; type: string; size: number } }>) => {
    if (picks.length === 0) return;
    const additions: PendingAttachment[] = picks.map(p => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: new File([], p.file.name, { type: p.file.type }),
      progress: 100,
      entityId: p.entityId,
    }));
    setPendingAttachments(prev => [...prev, ...additions]);
  }, []);

  const handleRemoveAttachment = useCallback((attId: string) => {
    setPendingAttachments(prev => {
      const att = prev.find(a => a.id === attId);
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      return prev.filter(a => a.id !== attId);
    });
  }, []);

  const handleEmojiInsert = useCallback((emoji: string) => {
    setMessageText(prev => prev + emoji);
  }, []);

  const handlePanelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizingRef.current) return;
      setPanelWidth(Math.max(280, Math.min(600, startWidth + (startX - ev.clientX))));
    };
    const onMouseUp = () => {
      isResizingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth]);

  const handleComposerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDelete = () => {
    if (!id) return;
    setConfirmDialog({
      title: t('chat.deleteConfirm'),
      destructive: true,
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        try {
          await chatApi.DeleteConversation(id);
          navigate('/chats');
        } catch {
          // silent
        }
      },
    });
  };

  // ---- Render ----

  if (isLoadingConv) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <ErrorAlert error={error} />
        <Link to="/chats" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!conversation) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/chats" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const typeInfo = conversationTypeMap[conversation.type ?? 'CONVERSATION_TYPE_UNSPECIFIED'];

  // Resolve conversation display via shared helper — for DMs this uses the
  // other participant's profile (never the unreliable backend `name` field).
  const display = resolveConversationDisplay(
    conversation,
    auth.activeProfileId,
    conversationMembers,
  );
  const displayTitle = display.name.startsWith('chat.') ? t(display.name) : display.name;
  const peerProfileId = display.isDirect
    ? conversationMembers.find(m => m.profile?.entity?.id && m.profile.entity.id !== auth.activeProfileId)
      ?.profile?.entity?.id
    : undefined;

  // Mention tag create/delete is admin-gated server-side (CreateMentionTag /
  // DeleteMentionTag require the "admin" relation on the conversation) —
  // mirror that here so the panel only shows management controls to
  // members who could actually use them.
  const myRole = conversationMembers.find(m => m.profile?.entity?.id === auth.activeProfileId)?.role;
  const canManageMentionTags = myRole === 'CONVERSATION_ROLE_OWNER' || myRole === 'CONVERSATION_ROLE_ADMIN';

  // Mobile: thread takes over the full screen
  if (isMobile && activePanel === 'thread' && threadParent && id) {
    return (
      <div className="flex h-full min-h-0 flex-col selection:bg-accent selection:text-accent-foreground">
        <ThreadPanel
          conversationId={id}
          parentMessage={threadParent}
          locale={locale}
          profileMap={profileMap}
          onClose={() => { setActivePanel(null); setThreadParent(null); }}
          onReaction={handleReaction}
          isMobile
          currentProfileId={auth.activeProfileId}
        />
      </div>
    );
  }

  return (
    // The height is the surrounding chat surface's, not the window's: this page
    // renders inside a pane that neither starts at the top of the window nor
    // scrolls with it.
    <div className="flex h-full min-h-0 flex-col selection:bg-accent selection:text-accent-foreground">
      {/* ---- Header ---- */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <Link to="/chats" className={ghostIconBtn}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </Link>
          {/* Conversation avatar. display.avatarUrl wins whenever set —
              the DM resolver already folds conversation.avatar_url over the
              peer's profile avatar, so a custom uploaded DM avatar lands
              here first. Peer profile avatar is the fallback. */}
          {display.isDirect && peerProfileId && !display.avatarUrl ? (
            <UserAvatar profileId={peerProfileId} profileMap={profileMap} />
          ) : (
            <EntityAvatar name={displayTitle} image={display.avatarUrl} profileId={display.peerProfileId} size="sm" className="h-9 w-9" />
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-foreground">
                {displayTitle}
              </h1>
              <Badge size="sm" variant="secondary">{t(typeInfo.labelKey)}</Badge>
              {/* Entity-bound discussion (task, document…) — link back to it */}
              {conversation.parent?.id && entityToRoute({ type_name: conversation.parent.type_name, id: conversation.parent.id }) && (
                <Link
                  to={entityToRoute({ type_name: conversation.parent.type_name, id: conversation.parent.id })}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title={t('chat.openEntity')}
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h6v6" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14L21 3" />
                  </svg>
                  {t('chat.openEntity')}
                </Link>
              )}
            </div>
            {conversation.description && (
              <p className="text-xs text-muted-foreground truncate max-w-md">
                {conversation.description}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Copy link to conversation — clipboard icon, consistent with
              the same action in the per-message hover bar. */}
          <button
            onClick={handleCopyConversationLink}
            className={ghostIconBtn}
            title={t('chat.copyLink')}
            aria-label={t('chat.copyLink')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
            </svg>
          </button>
          {/* Search within this conversation (chat.SearchMessages) */}
          <button
            onClick={() => setActivePanel(activePanel === 'search' ? null : 'search')}
            className={`${ghostIconBtn} ${
              activePanel === 'search' ? 'bg-accent text-foreground' : ''
            }`}
            title={t('chat.search.titleScoped')}
            aria-label={t('chat.search.titleScoped')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </button>
          {/* AI conversation summary (chat.GetConversationSummary) */}
          <button
            onClick={() => setActivePanel(activePanel === 'summary' ? null : 'summary')}
            className={`${ghostIconBtn} ${
              activePanel === 'summary' ? 'bg-accent text-foreground' : ''
            }`}
            title={t('chat.summary.title')}
            aria-label={t('chat.summary.title')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
            </svg>
          </button>
          {/* Mention tags (chat.ListMentionTags / SubscribeToMentionTag) */}
          <button
            onClick={() => setActivePanel(activePanel === 'mentionTags' ? null : 'mentionTags')}
            className={`${ghostIconBtn} ${
              activePanel === 'mentionTags' ? 'bg-accent text-foreground' : ''
            }`}
            title={t('chat.mentionTags.title')}
            aria-label={t('chat.mentionTags.title')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 8.25H9m6 3H9m3 8.25-4.5-4.5H4.5a2.25 2.25 0 0 1-2.25-2.25V6.75A2.25 2.25 0 0 1 4.5 4.5h15a2.25 2.25 0 0 1 2.25 2.25v6.75a2.25 2.25 0 0 1-2.25 2.25h-3.75l-4.5 4.5Z" />
            </svg>
          </button>
          {/* Attachments (photo stack icon, distinct from the pin icon). */}
          <button
            onClick={() => setActivePanel(activePanel === 'attachments' ? null : 'attachments')}
            className={`${ghostIconBtn} ${
              activePanel === 'attachments' ? 'bg-accent text-foreground' : ''
            }`}
            title={t('chat.attachments')}
            aria-label={t('chat.attachments')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
            </svg>
          </button>
          {/* Pinned messages — pin icon, distinct from the paperclip
              used by the file-attach button in the composer. */}
          <button
            onClick={() => setActivePanel(activePanel === 'pinned' ? null : 'pinned')}
            className={`${ghostIconBtn} ${
              activePanel === 'pinned' ? 'bg-accent text-foreground' : ''
            }`}
            title={t('chat.pinnedMessages')}
            aria-label={t('chat.pinnedMessages')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 17v5" />
              <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1Z" />
            </svg>
          </button>
          {/* Members */}
          <button
            onClick={() => setActivePanel(activePanel === 'members' ? null : 'members')}
            className={`${ghostIconBtn} ${
              activePanel === 'members' ? 'bg-accent text-foreground' : ''
            }`}
            title={t('chat.members', { count: conversation.member_count ?? 0 })}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
            </svg>
            {activePanel === 'members' && (
              <span className="ml-1 text-xs tabular-nums">{conversation.member_count}</span>
            )}
          </button>
          {/* Settings */}
          <Link
            to={`/chats/${id}/edit`}
            className={ghostIconBtn}
            title={t('common.edit')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </Link>
          {/* Delete */}
          <button
            onClick={handleDelete}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
            title={t('common.delete')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      </div>

      {/* ---- Body ---- */}
      <div className="flex flex-1 overflow-hidden">
        {/* Messages area */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Message list */}
          <div
            ref={messagesContainerRef}
            className="scrollbar-slim flex-1 overflow-x-hidden overflow-y-auto"
            onScroll={handleScroll}
          >
            {/* Load more indicator */}
            {hasMoreMessages && (
              <div className="flex justify-center py-3">
                <button
                  onClick={() => loadMessages(true)}
                  className="text-xs text-primary hover:underline"
                >
                  {t('chat.loadMoreMessages')}
                </button>
              </div>
            )}

            {isLoadingMessages ? (
              <div className="space-y-4 p-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="h-9 w-9 rounded-full bg-muted flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <div className="h-3 w-20 rounded bg-muted" />
                        <div className="h-3 w-12 rounded bg-muted" />
                      </div>
                      <div className="h-4 w-3/4 rounded bg-muted" />
                    </div>
                  </div>
                ))}
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                  <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-foreground">{t('chat.noMessagesYet')}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t('chat.startConversation')}</p>
              </div>
            ) : (
              <div className="mx-auto w-full max-w-4xl overflow-x-hidden py-4">
                {messages.map((msg, idx) => {
                  const senderId = msg.sender?.entity?.id;
                  const isOwn = !!senderId && senderId === auth.activeProfileId;
                  const prev = idx > 0 ? messages[idx - 1] : null;
                  const prevSenderId = prev?.sender?.entity?.id;
                  const curTime = getMessageTimeMs(msg);
                  const prevTime = prev ? getMessageTimeMs(prev) : 0;

                  // Day separator: first message always gets one (with its own
                  // date), and any message whose local day differs from the
                  // previous one opens a new section.
                  const showDaySeparator =
                    curTime > 0 && (idx === 0 || !sameLocalDay(prevTime, curTime));

                  // Group ALL consecutive messages from the same sender until the
                  // sender changes or the local day flips. We deliberately do NOT
                  // time-gate the grouping: a quiet 20-minute gap between two
                  // messages from the same author should not re-print the header
                  // and avatar — the per-message timestamp inside the bubble row
                  // is enough to tell the reader when each line was sent.
                  const sameGroup =
                    prev != null &&
                    prevSenderId === senderId &&
                    !showDaySeparator;
                  const showSender = !sameGroup;

                  return (
                    <Fragment key={msg.entity?.id}>
                      {showDaySeparator && (
                        <DaySeparator label={formatDaySeparator(curTime, locale, t)} />
                      )}
                      <MessageItem
                        message={msg}
                        locale={locale}
                        profileMap={profileMap}
                        isOwn={isOwn}
                        showSender={showSender}
                        onReply={handleOpenThread}
                        onQuickReply={handleQuickReply}
                        onEdit={handleStartEdit}
                        onDelete={handleDeleteMessage}
                        onPin={handlePinMessage}
                        onReaction={handleReaction}
                        onLink={handleOpenLinkPicker}
                        onCreateTask={handleOpenCreateTask}
                        linkedResources={msg.entity?.id ? messageResources.get(msg.entity.id) : undefined}
                        onUnlinkResource={handleUnlinkResource}
                        onTag={handleOpenTagPicker}
                        tags={msg.entity?.id ? messageTags.get(msg.entity.id) : undefined}
                        selectionMode={selectionMode}
                        isSelected={!!msg.entity?.id && selectedIds.has(msg.entity.id)}
                        onToggleSelect={handleToggleSelect}
                        onCopyLink={handleCopyMessageLink}
                        onShowReaders={handleShowReaders}
                        highlighted={!!msg.entity?.id && msg.entity.id === highlightedMessageId}
                      />
                    </Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Typing indicator — shown as a thin banner above the composer
              when any member (other than self) has typed recently. */}
          {typingLabel && (
            <div className="border-t border-border bg-background/50">
              <div className="mx-auto flex w-full max-w-4xl items-center gap-2 px-4 py-1.5 text-xs text-muted-foreground">
                <TypingDots />
                <span>{typingLabel}</span>
              </div>
            </div>
          )}

          {/* Bulk selection action bar — shown while selectionMode is on */}
          {selectionMode && (
            <div className="border-t border-border bg-muted">
              <div className="mx-auto flex w-full max-w-4xl items-center gap-2 px-4 py-2">
                <span className="text-xs font-medium text-foreground">
                  {t('chat.selectedCount', { count: selectedIds.size })}
                </span>
                <div className="flex-1" />
                <button
                  onClick={handleBulkCopy}
                  disabled={selectedIds.size === 0}
                  className="rounded-md px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
                >
                  {t('chat.copy')}
                </button>
                <button
                  onClick={handleOpenForwardPicker}
                  disabled={selectedIds.size === 0}
                  className="rounded-md px-2 py-1 text-xs text-primary hover:bg-primary/10 disabled:opacity-50"
                >
                  {t('chat.forward')}
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={selectedIds.size === 0}
                  className="rounded-md px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                >
                  {t('common.delete')}
                </button>
                <button
                  onClick={handleExitSelection}
                  className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Reply chip above the composer — the same quote element the sent
              message will carry, so what you are about to send looks like what
              you will see once it is sent. */}
          {replyTo && (
            <div className="border-t border-border bg-muted/30">
              <div className="mx-auto flex w-full max-w-4xl items-start gap-2 px-4 py-2">
                <div className="min-w-0 flex-1 rounded-md border border-border bg-muted px-2 py-1">
                  <div className="truncate text-[11px] font-semibold text-foreground">
                    {t('chat.replyingTo', {
                      name: (replyTo.sender?.entity?.id && profileMap.get(replyTo.sender.entity.id)?.displayName)
                        || replyTo.sender?.display_name
                        || t('chat.unknownUser'),
                    })}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {replyTo.content?.slice(0, 200)}
                  </div>
                </div>
                <button
                  onClick={() => setReplyTo(null)}
                  className={ghostIconBtn}
                  title={t('common.cancel')}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Edit message bar */}
          {editingMessage && (
            <div className="border-t border-border bg-muted/30">
             <div className="mx-auto w-full max-w-4xl px-4 py-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('chat.editingMessage')}</span>
                <button
                  onClick={() => { setEditingMessage(null); setEditText(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {t('common.cancel')}
                </button>
              </div>
              <div className="mt-1 flex gap-2">
                <textarea
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditMessage(); }
                    if (e.key === 'Escape') { setEditingMessage(null); setEditText(''); }
                  }}
                  rows={1}
                  className="flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                />
                <Button size="sm" onClick={handleEditMessage} disabled={!editText.trim()}>
                  {t('common.save')}
                </Button>
              </div>
             </div>
            </div>
          )}

          {/* Message composer */}
          {!conversation.is_readonly && !conversation.is_archived && (
            <div className="border-t border-border">
             <div className="mx-auto w-full max-w-4xl px-4 py-3">
              {/* Scheduled bar + armed-schedule chip */}
              {id && (
                <ScheduledMessagesBar
                  conversationId={id}
                  refreshKey={scheduledRefreshKey}
                  locale={locale}
                />
              )}
              {scheduledAt && (
                <ScheduledChip
                  scheduledAt={scheduledAt}
                  locale={locale}
                  onClear={() => setScheduledAt(null)}
                />
              )}

              {/* Pending attachments */}
              {pendingAttachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {pendingAttachments.map(att => (
                    <PendingAttachmentCard
                      key={att.id}
                      attachment={att}
                      onRemove={handleRemoveAttachment}
                    />
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <div className="flex-1 rounded-lg border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
                  <textarea
                    value={messageText}
                    onChange={e => { setMessageText(e.target.value); handleTyping(); }}
                    onKeyDown={handleComposerKeyDown}
                    placeholder={t('chat.messagePlaceholder')}
                    rows={1}
                    className="w-full resize-none bg-transparent px-3 py-2.5 text-sm text-foreground caret-primary placeholder:text-muted-foreground focus:outline-none"
                  />
                  {/* Toolbar */}
                  <div className="flex items-center gap-0.5 border-t border-border px-2 py-1">
                    {/* Emoji */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowEmojiPickerComposer(!showEmojiPickerComposer)}
                        className={ghostIconBtn}
                        title={t('chat.addReaction')}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z" />
                        </svg>
                      </button>
                      {showEmojiPickerComposer && (
                        <ComposerEmojiPicker
                          onSelect={handleEmojiInsert}
                          onClose={() => setShowEmojiPickerComposer(false)}
                        />
                      )}
                    </div>
                    {/* Attach file — label wraps the input so the click
                        opens the native picker without going through a
                        ref.click() shim that some browsers block. */}
                    <label
                      className={`cursor-pointer ${ghostIconBtn}`}
                      title={t('chat.attachFile')}
                      aria-label={t('chat.attachFile')}
                    >
                      <input
                        type="file"
                        multiple
                        className="sr-only"
                        onChange={e => { handleFilesSelected(e.target.files); e.target.value = ''; }}
                      />
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
                      </svg>
                    </label>
                    {/* Browse existing attachments — re-share without re-upload */}
                    <button
                      type="button"
                      onClick={() => setAttachmentBrowserOpen(true)}
                      className={ghostIconBtn}
                      title={t('chat.browseAttachments')}
                      aria-label={t('chat.browseAttachments')}
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                      </svg>
                    </button>
                    {/* Attach image — same label-wraps-input pattern. */}
                    <label
                      className={`cursor-pointer ${ghostIconBtn}`}
                      title={t('chat.attachImage')}
                      aria-label={t('chat.attachImage')}
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="sr-only"
                        onChange={e => { handleFilesSelected(e.target.files); e.target.value = ''; }}
                      />
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25a2.25 2.25 0 0 0-2.25-2.25H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Zm16.5-13.5h.008v.008h-.008V7.5ZM12 10.5a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                      </svg>
                    </label>
                  </div>
                </div>
                <div className="flex items-end gap-1 self-end">
                  <Button
                    onClick={handleSendMessage}
                    disabled={(!messageText.trim() && pendingAttachments.length === 0) || isSending}
                    isLoading={isSending}
                  >
                    {t('chat.send')}
                  </Button>
                  <ScheduleSendButton
                    scheduledAt={scheduledAt}
                    onSchedule={setScheduledAt}
                    disabled={isSending}
                  />
                </div>
              </div>
             </div>
            </div>
          )}

          {/* Archived/readonly banner */}
          {(conversation.is_readonly || conversation.is_archived) && (
            <div className="border-t border-border bg-muted/50">
              <div className="mx-auto w-full max-w-4xl px-4 py-3 text-center text-sm text-muted-foreground">
                {conversation.is_archived ? t('chat.archivedBanner') : t('chat.readonlyBanner')}
              </div>
            </div>
          )}
        </div>

        {/* ---- Side panels ---- */}
        {activePanel === 'thread' && threadParent && id && !isMobile && (
          <>
            {/* Resize handle */}
            <div
              className="w-1 flex-shrink-0 cursor-col-resize bg-transparent hover:bg-primary/20 active:bg-primary/40 transition-colors"
              onMouseDown={handlePanelResizeStart}
            />
            <div style={{ width: panelWidth }} className="flex-shrink-0 h-full">
              <ThreadPanel
                conversationId={id}
                parentMessage={threadParent}
                locale={locale}
                profileMap={profileMap}
                onClose={() => { setActivePanel(null); setThreadParent(null); }}
                onReaction={handleReaction}
                currentProfileId={auth.activeProfileId}
              />
            </div>
          </>
        )}

        {activePanel === 'members' && id && (
          <MembersPanel
            conversationId={id}
            profileMap={profileMap}
            onClose={() => setActivePanel(null)}
          />
        )}

        {activePanel === 'pinned' && id && (
          <PinnedMessagesPanel
            conversationId={id}
            locale={locale}
            profileMap={profileMap}
            messagesById={new Map(messages.filter(m => m.entity?.id).map(m => [m.entity!.id!, m]))}
            onClose={() => setActivePanel(null)}
            onUnpin={handleUnpinMessage}
            onJump={(msgId) => {
              setHighlightedMessageId(msgId);
              const el = document.querySelector(`[data-message-id="${msgId}"]`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              window.setTimeout(() => setHighlightedMessageId(null), 2000);
            }}
          />
        )}

        {activePanel === 'attachments' && id && (
          <AttachmentsPanel
            messages={messages}
            locale={locale}
            onClose={() => setActivePanel(null)}
            onJump={(msgId) => {
              setHighlightedMessageId(msgId);
              const el = document.querySelector(`[data-message-id="${msgId}"]`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              window.setTimeout(() => setHighlightedMessageId(null), 2000);
            }}
          />
        )}

        {activePanel === 'search' && id && (
          <MessageSearchPanel
            scopeConversationId={id}
            locale={locale}
            onClose={() => setActivePanel(null)}
            onJump={(conversationId, msgId) => {
              if (conversationId === id) {
                setActivePanel(null);
                setHighlightedMessageId(msgId);
                const el = document.querySelector(`[data-message-id="${msgId}"]`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  window.setTimeout(() => setHighlightedMessageId(null), 2000);
                } else {
                  navigate(`/chats/${conversationId}?m=${msgId}`);
                }
              } else {
                navigate(`/chats/${conversationId}?m=${msgId}`);
              }
            }}
          />
        )}

        {activePanel === 'summary' && id && (
          <ConversationSummaryPanel
            conversationId={id}
            locale={locale}
            onClose={() => setActivePanel(null)}
          />
        )}

        {activePanel === 'mentionTags' && id && (
          <MentionTagsPanel
            conversationId={id}
            canManage={canManageMentionTags}
            onClose={() => setActivePanel(null)}
          />
        )}
      </div>

      {/* Resource picker modal (framework-mew) — mounted at root so it
          portals over everything. ResourceCommandMenu calls LinkResources
          itself on selection and then fires onLinked with the picked
          Resource, which we splice into the local cache. */}
      <ResourceCommandMenu
        isOpen={linkingMessage !== null}
        onClose={() => setLinkingMessage(null)}
        entityUrn={entityToUrn(linkingMessage?.entity)}
        entityType={linkingMessage?.entity?.type_id ?? 'message'}
        onLinked={handleResourceLinked}
      />

      {/* Tag picker modal (framework-zak). Reuses the existing TagPicker
          component (multi-select with create-on-the-fly) inside a thin
          modal scoped to one message. The picker's onChange diffs the
          new tag set against the cached one and fires TagEntity /
          UntagEntity in parallel. */}
      {taggingMessage && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setTaggingMessage(null)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md rounded-lg border border-border bg-background p-4 shadow-xl mx-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">{t('chat.tagMessage')}</h3>
              <button
                onClick={() => setTaggingMessage(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('common.close')}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <TagPicker
              value={(messageTags.get(taggingMessage.entity?.id ?? '') ?? []).map(s => ({
                // Tag has a nested entity object (id lives at entity.id),
                // not a flat id field. TagPicker reads name/color/icon for
                // display and matches selection by entity.id, so this
                // light shim is enough — it never renders other Tag
                // fields like description, scope_team, created_by, etc.
                entity: { id: s.id, type_id: 'tag', type_name: 'Tag', platform_id: '', tenant_id: '', product_id: '' },
                name: s.name,
                color: s.color,
                icon: s.icon,
                visibility: s.visibility,
                usage_count: 0,
              } as Tag))}
              onChange={handleTagPickerChange}
              placeholder={t('chat.tagPickerPlaceholder')}
            />
          </div>
        </div>
      )}

      {/* Forward picker modal — simple list of the user's conversations with
          a multi-select checkbox row. ForwardMessages is called server-side
          with the selected targets and message ids; each target gets a
          content copy with ForwardedFrom provenance. */}
      <CreateTaskFromMessage
        open={taskMessage !== null}
        onOpenChange={(v) => { if (!v) setTaskMessage(null); }}
        message={taskMessage}
        conversationId={id}
        parentEntity={
          conversation?.parent?.id
            ? { id: conversation.parent.id, type_name: conversation.parent.type_name || '' }
            : null
        }
      />

      <ConfirmDialog
        open={confirmDialog !== null}        title={confirmDialog?.title ?? ''}
        description={confirmDialog?.description}
        confirmLabel={confirmDialog?.confirmLabel}
        destructive={confirmDialog?.destructive}
        isConfirming={isConfirming}
        onCancel={() => { if (!isConfirming) setConfirmDialog(null); }}
        onConfirm={async () => {
          if (!confirmDialog) return;
          setIsConfirming(true);
          try {
            await confirmDialog.onConfirm();
          } finally {
            setIsConfirming(false);
            setConfirmDialog(null);
          }
        }}
      />

      <AttachmentBrowser
        open={attachmentBrowserOpen}
        onClose={() => setAttachmentBrowserOpen(false)}
        onPick={handleAttachExisting}
      />

      {readersModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setReadersModal(null)}
            aria-hidden="true"
          />
          <div className="relative mx-4 w-full max-w-sm rounded-lg border border-border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">
                {t('chat.readBy')}
              </h3>
              <button
                onClick={() => setReadersModal(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('common.close')}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2">
              {readersModal.loading ? (
                <div className="p-4 text-center text-xs text-muted-foreground">{t('common.loading')}</div>
              ) : !readersModal.readers || readersModal.readers.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">{t('chat.noReadersYet')}</div>
              ) : (
                <ul className="flex flex-col">
                  {readersModal.readers.map(r => {
                    const p = profileMap.get(r.profile_id);
                    return (
                      <li key={r.profile_id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent">
                        <UserAvatar profileId={r.profile_id} profileMap={profileMap} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">
                            {p?.displayName || r.profile_id}
                          </div>
                          <div className="text-[11px] tabular-nums text-muted-foreground">
                            {formatMessageTime(r.read_at, locale)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {forwardPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setForwardPickerOpen(false)}
            aria-hidden="true"
          />
          <div className="relative mx-4 w-full max-w-md rounded-lg border border-border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">
                {t('chat.forwardTo', { count: selectedIds.size })}
              </h3>
              <button
                onClick={() => setForwardPickerOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('common.close')}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {forwardConversations.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t('common.loading')}
                </div>
              ) : (
                forwardConversations
                  .filter(c => c.entity?.id && c.entity.id !== id)
                  .map(c => {
                    const cid = c.entity!.id!;
                    const checked = forwardTargets.has(cid);
                    return (
                      <button
                        key={cid}
                        onClick={() => handleToggleForwardTarget(cid)}
                        className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                          checked ? 'bg-primary/10' : 'hover:bg-accent'
                        }`}
                      >
                        <div className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
                        }`}>
                          {checked && (
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                            </svg>
                          )}
                        </div>
                        <span className="min-w-0 flex-1 truncate text-foreground">
                          {c.name || c.entity?.id}
                        </span>
                      </button>
                    );
                  })
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
              <button
                onClick={() => setForwardPickerOpen(false)}
                className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
              >
                {t('common.cancel')}
              </button>
              <Button
                size="sm"
                onClick={handleConfirmForward}
                disabled={forwardTargets.size === 0 || isForwarding}
                isLoading={isForwarding}
              >
                {t('chat.forward')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ChatDetailPage;
